import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Eye,
  Loader2,
  Play,
  Pause,
  Archive,
  Plus,
  X,
  Download,
} from 'lucide-react';
import {
  adsApi,
  campaignsApi,
  type CampaignScheduleFilterParams,
  type ProfileCampaignListFilters,
} from '@/api/client';
import {
  campaignAdProductLabel,
  campaignAdProductToBudgetRuleApiProduct,
  normalizeSpCampaign,
  type CampaignAdProduct,
  type SpCampaignNormalized,
} from '@/types/spCampaigns';
import {
  BudgetRuleListCard,
  extractBudgetRulesList,
  getBudgetRulesNextToken,
} from '@/components/BudgetRuleListCard';
import { BudgetRuleEditModal } from '@/components/BudgetRuleEditModal';
import {
  buildCreateBudgetRulePayload,
  buildStateOnlyUpdatePayload,
  type BudgetRuleKind,
} from '@/utils/spBudgetRules';
import { CAMPAIGN_SEARCH_PLACEHOLDER, filterCampaignsBySearch } from '@/utils/campaignSearch';
import { uniformWeeklyHourlyEdit } from '@/utils/hourlyBudgetWeek';
import { SpReportCompareTab } from '@/components/SpReportCompareTab';
import { SpRecommendationsTab } from '@/components/SpRecommendationsTab';
import { CampaignTypePickerModal } from '@/components/campaign-create/CampaignTypePickerModal';
import {
  campaignReportLookupKey,
  KEYWORD_REPORT_AD_PRODUCT_IDS,
  keywordReportAdProductsQueryParam,
  REPORT_AD_PRODUCT_OPTIONS,
  reportAdProductShortLabel,
  reportAdProductsQueryParam,
  TARGET_REPORT_AD_PRODUCT_IDS,
  targetReportAdProductsQueryParam,
} from '@/api/reportAdProducts';
import { parseReportSummaryPayload } from '@/api/reportSummary';
import {
  exportCampaignReportCsv,
  exportKeywordReportCsv,
  exportTargetReportCsv,
} from '@/api/reportCsvExport';
import {
  CAMPAIGN_REPORT_COLUMNS,
  DEFAULT_CAMPAIGN_REPORT_COLUMN_IDS,
  DEFAULT_KEYWORD_REPORT_COLUMN_IDS,
  DEFAULT_TARGET_REPORT_COLUMN_IDS,
  KEYWORD_REPORT_COLUMNS,
  TARGET_REPORT_COLUMNS,
  visibleCampaignReportColumns,
  visibleKeywordReportColumns,
  visibleTargetReportColumns,
  campaignReportColumnLabel,
  keywordReportColumnLabel,
  targetReportColumnLabel,
  type CampaignReportColumnId,
  type KeywordReportColumnId,
  type TargetReportColumnId,
} from '@/api/reportTableColumns';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { getDefaultDashboardDateRange } from '@/api/reportDateRanges';
import {
  buildKeywordMetricsMap,
  buildTargetMetricsMap,
  formatCurrencyValue,
  formatNumberValue,
  formatPercentValue,
  metricsFromKeywordSources,
  metricsFromReportRow,
  parseKeywordsPayload,
  parseNumberish,
  parseReportRowsPayload,
  targetMetricsForCatalogRow,
  type ReportRow,
} from '@/api/keywordMetrics';
import {
  formatTargetExpression,
  parseSpTargetsPayload,
  type SpTargetRow,
} from '@/api/spTargets';
import {
  dashboardPhaseReached,
  useDashboardSequentialLoad,
} from '@/api/dashboardLoad';
import {
  buildKeywordCampaignFilterOptions,
  filterUnifiedKeywordsByCampaign,
  mergeUnifiedKeywords,
  resolveUnifiedCampaignNames,
} from '@/api/unifiedKeywords';
import { resolveEditableUnifiedKeywordBid } from '@/api/keywordBidUpdate';
import {
  resolveEditableSbTargetBid,
  resolveEditableSpTargetBid,
} from '@/api/targetBidUpdate';
import { EditableKeywordBidCell } from '@/components/EditableKeywordBidCell';
import { EditableTargetBidCell } from '@/components/EditableTargetBidCell';
import { EditableCampaignBudgetCell } from '@/components/EditableCampaignBudgetCell';
import {
  BulkCampaignBudgetConfirmModal,
  type BulkCampaignBudgetPreviewRow,
} from '@/components/BulkCampaignBudgetConfirmModal';
import {
  applyBulkCampaignBudgetUpdates,
  CAMPAIGN_BUDGET_ADJUST_MODES,
  campaignBudgetAdjustModeLabel,
  campaignSelectionKey,
  computeAdjustedBudget,
  resolveEditableCampaignBudget,
  summarizeBulkBudgetResults,
  type CampaignBudgetAdjustMode,
} from '@/api/campaignBudgetUpdate';
import { useToast } from '@/components/ui/Toast';
import { AccountDashboard } from '@/components/dashboard/AccountDashboard';
import { BudgetUsageSection } from '@/components/dashboard/BudgetUsageSection';
import { PortfoliosSection } from '@/components/dashboard/PortfoliosSection';
import { KeywordCampaignNamesCell } from '@/components/dashboard/KeywordCampaignNamesCell';
import { AccountPageHeader } from '@/components/dashboard/AccountPageHeader';
import { PageSection } from '@/components/ui/PageSection';
import { LazyMount } from '@/components/ui/LazyMount';
import { VirtualizedTable } from '@/components/ui/VirtualizedTableBody';
import { Badge } from '@/components/ui/Badge';
import { Spinner, LoadingScreen } from '@/components/ui/Spinner';
import type { Portfolio } from '@/types/portfolios';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

function formatCurrency(amount: number) {
  return formatAccountCurrency(amount);
}

function toIsoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function formatDashboardDateRangeLabel(startDate: string, endDate: string): string {
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

function getDefaultReportDateRange(): { startDate: string; endDate: string } {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (end < start) {
    // On the 1st day of a month there is no "current month to yesterday" range.
    // Default to the full previous month so reports tab shows meaningful data.
    const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const prevMonthStart = new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), 1);
    return { startDate: toIsoDate(prevMonthStart), endDate: toIsoDate(prevMonthEnd) };
  }
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

function parseCampaignsPayload(data: unknown): SpCampaignNormalized[] {
  if (Array.isArray(data)) {
    return data
      .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
      .map((x) => normalizeSpCampaign(x));
  }
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const rawList = (d.campaigns as unknown[]) || (d.items as unknown[]) || [];
  if (!Array.isArray(rawList)) return [];
  return rawList
    .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
    .map((x) => normalizeSpCampaign(x));
}

function profileCampaignsListPath(profileId: string): string {
  return `/accounts/${encodeURIComponent(profileId)}/campaigns`;
}

function profileCampaignDetailPath(
  profileId: string,
  campaignId: string,
  adProduct: CampaignAdProduct = 'SP'
): string {
  const base = `${profileCampaignsListPath(profileId)}/${encodeURIComponent(campaignId)}`;
  if (adProduct && adProduct !== 'SP') {
    return `${base}?product=${encodeURIComponent(adProduct)}`;
  }
  return base;
}

type CampaignStatusFilter = 'ALL' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
type CampaignAdProductFilter = 'ALL' | CampaignAdProduct;
type CampaignPortfolioFilter = 'ALL' | 'NONE' | string;
type ActiveTab =
  | 'dashboard'
  | 'campaigns'
  | 'ad-groups'
  | 'keywords'
  | 'targets'
  | 'budget-rules'
  | 'reports'
  | 'compare'
  | 'recommendations'
  | 'stream';
type AdGroupStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
type KeywordStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
type TargetStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';

function accountTabTitle(tab: ActiveTab): string {
  const titles: Partial<Record<ActiveTab, string>> = {
    dashboard: 'Campaigns',
    campaigns: 'Campaigns',
    'ad-groups': 'Ad groups',
    keywords: 'Keywords',
    targets: 'Targets',
    'budget-rules': 'Budget rules',
    reports: 'Reports',
    compare: 'Compare',
    recommendations: 'Recommendations',
    stream: 'Dayparting',
  };
  return titles[tab] ?? 'Campaigns';
}

function parseActiveTabFromPath(pathname: string): ActiveTab {
  const parts = pathname.split('/').filter(Boolean);
  const tab = parts.length > 0 ? parts[parts.length - 1] : 'dashboard';
  if (
    tab === 'dashboard' ||
    tab === 'campaigns' ||
    tab === 'ad-groups' ||
    tab === 'keywords' ||
    tab === 'targets' ||
    tab === 'budget-rules' ||
    tab === 'reports' ||
    tab === 'compare' ||
    tab === 'recommendations' ||
    tab === 'stream'
  ) {
    return tab;
  }
  return 'dashboard';
}

/** Tabs that load unified SP/SB/SD campaigns (and catalog prefetch). Others use their own APIs only. */
function tabNeedsProfileCampaigns(tab: ActiveTab): boolean {
  return (
    tab === 'dashboard' ||
    tab === 'campaigns' ||
    tab === 'ad-groups' ||
    tab === 'keywords' ||
    tab === 'targets' ||
    tab === 'budget-rules'
  );
}

type ReportWindowStatus = {
  id?: string;
  window_start?: string;
  window_end?: string;
  report_type_id?: string;
  report_family?: string;
  status?: string;
  imported_row_count?: number;
  is_cached?: boolean;
};
type ReportBatchStatus = {
  id?: string;
  status?: string;
  completed_window_count?: number;
  failed_window_count?: number;
  cached_window_count?: number;
  requested_range_start?: string;
  requested_range_end?: string;
};
type SbAdGroupRow = {
  ad_group_id?: string;
  campaign_id?: string;
  name?: string;
  state?: string;
  extended_data?: {
    serving_status?: string;
    serving_status_details?: string[];
  } | null;
};

type SpAdGroupRowUi = {
  ad_group_id?: string;
  campaign_id?: string;
  name?: string;
  state?: string;
  default_bid?: number;
  extended_data?: {
    serving_status?: string;
    status_reasons?: string[];
  } | null;
};

type SdAdGroupRowUi = SpAdGroupRowUi & { creative_type?: string; bid_optimization?: string };

function getRawValue(raw: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return null;
}

function getMetric(raw: Record<string, unknown>, keys: string[]): number | null {
  return parseNumberish(getRawValue(raw, keys));
}

function formatDateValue(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '-';
  return value;
}

function parseReportBatchPayload(data: unknown): {
  batch: ReportBatchStatus | null;
  requests: ReportWindowStatus[];
  reusedActiveBatch: boolean;
} {
  if (!data || typeof data !== 'object') {
    return { batch: null, requests: [], reusedActiveBatch: false };
  }
  const raw = data as Record<string, unknown>;
  return {
    batch: raw.batch && typeof raw.batch === 'object' ? (raw.batch as ReportBatchStatus) : null,
    requests: Array.isArray(raw.requests) ? (raw.requests as ReportWindowStatus[]) : [],
    reusedActiveBatch: Boolean(raw.reused_active_batch ?? raw.reusedActiveBatch),
  };
}

function renderCampaignReportCell(columnId: CampaignReportColumnId, row: ReportRow): string {
  switch (columnId) {
    case 'type':
      return reportAdProductShortLabel(row.adProduct || 'sponsored_products');
    case 'campaign':
      return row.campaignName || '-';
    case 'impressions':
      return formatNumberValue(parseNumberish(row.impressions) ?? null, 0);
    case 'clicks':
      return formatNumberValue(parseNumberish(row.clicks) ?? null, 0);
    case 'spend':
      return formatCurrencyValue(parseNumberish(row.cost) ?? null);
    case 'purchases':
      return formatNumberValue(parseNumberish(row.purchases30d) ?? null, 0);
    case 'sales':
      return formatCurrencyValue(parseNumberish(row.sales30d) ?? null);
    case 'ctr':
      return formatPercentValue(parseNumberish(row.ctr) ?? null);
    case 'roas':
      return formatNumberValue(parseNumberish(row.roas) ?? null);
    default:
      return '-';
  }
}

function renderKeywordReportCell(columnId: KeywordReportColumnId, row: ReportRow): string {
  switch (columnId) {
    case 'type':
      return reportAdProductShortLabel(row.adProduct || 'sponsored_products');
    case 'keyword':
      return row.keywordText || '-';
    case 'match':
      return row.matchType || '-';
    case 'impressions':
      return formatNumberValue(parseNumberish(row.impressions) ?? null, 0);
    case 'clicks':
      return formatNumberValue(parseNumberish(row.clicks) ?? null, 0);
    case 'spend':
      return formatCurrencyValue(parseNumberish(row.cost) ?? null);
    case 'purchases':
      return formatNumberValue(parseNumberish(row.purchases30d) ?? null, 0);
    case 'sales':
      return formatCurrencyValue(parseNumberish(row.sales30d) ?? null);
    case 'ctr':
      return formatPercentValue(parseNumberish(row.ctr) ?? null);
    case 'roas':
      return formatNumberValue(parseNumberish(row.roas) ?? null);
    default:
      return '-';
  }
}

function renderTargetReportCell(columnId: TargetReportColumnId, row: ReportRow): string {
  switch (columnId) {
    case 'target':
      return row.targetId || '-';
    case 'targeting':
      return row.targetingText || '-';
    case 'campaign':
      return row.campaignName || row.campaignId || '-';
    case 'adGroup':
      return row.adGroupId || '-';
    case 'impressions':
      return formatNumberValue(parseNumberish(row.impressions) ?? null, 0);
    case 'clicks':
      return formatNumberValue(parseNumberish(row.clicks) ?? null, 0);
    case 'spend':
      return formatCurrencyValue(parseNumberish(row.cost) ?? null);
    case 'purchases':
      return formatNumberValue(parseNumberish(row.purchases30d) ?? null, 0);
    case 'sales':
      return formatCurrencyValue(parseNumberish(row.sales30d) ?? null);
    case 'ctr':
      return formatPercentValue(parseNumberish(row.ctr) ?? null);
    case 'roas':
      return formatNumberValue(parseNumberish(row.roas) ?? null);
    default:
      return '-';
  }
}

function reportTableCellClassName(
  columnId: CampaignReportColumnId | KeywordReportColumnId | TargetReportColumnId
): string {
  if (columnId === 'type' || columnId === 'match') {
    return 'px-3 py-2 text-foreground-muted whitespace-nowrap text-xs';
  }
  if (columnId === 'target' || columnId === 'adGroup') {
    return 'px-3 py-2 text-foreground-muted whitespace-nowrap font-mono text-xs';
  }
  return 'px-3 py-2 text-foreground-muted whitespace-nowrap';
}

function parseAdGroupsPayload(data: unknown): { adGroups: SbAdGroupRow[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { adGroups: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const adGroups = Array.isArray(raw.ad_groups) ? (raw.ad_groups as SbAdGroupRow[]) : [];
  const totalResults =
    typeof raw.total_results === 'number' ? raw.total_results : adGroups.length;
  return { adGroups, totalResults };
}

function parseSdAdGroupsPayload(data: unknown): { adGroups: SdAdGroupRowUi[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { adGroups: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const adGroups = Array.isArray(raw.ad_groups) ? (raw.ad_groups as SdAdGroupRowUi[]) : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : adGroups.length;
  return { adGroups, totalResults };
}

function parseSpAdGroupsPayload(data: unknown): { adGroups: SpAdGroupRowUi[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { adGroups: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const adGroups = Array.isArray(raw.ad_groups) ? (raw.ad_groups as SpAdGroupRowUi[]) : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : adGroups.length;
  return { adGroups, totalResults };
}

function parseSdTargetsPayload(data: unknown): { targets: SpTargetRow[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { targets: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const targets = Array.isArray(raw.targets) ? (raw.targets as SpTargetRow[]) : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : targets.length;
  return { targets, totalResults };
}

type SdProductAdRow = {
  ad_id?: string;
  campaign_id?: string;
  ad_group_id?: string;
  asin?: string;
  landing_page_url?: string;
  state?: string;
  ad_name?: string;
};

function parseSdProductAdsPayload(data: unknown): { productAds: SdProductAdRow[]; totalResults: number } {
  if (!data || typeof data !== 'object') {
    return { productAds: [], totalResults: 0 };
  }
  const raw = data as Record<string, unknown>;
  const productAds = Array.isArray(raw.product_ads)
    ? (raw.product_ads as SdProductAdRow[])
    : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : productAds.length;
  return { productAds, totalResults };
}

function HourlyModal({
  campaign,
  profileId,
  isOpen,
  onClose,
}: {
  campaign: SpCampaignNormalized;
  profileId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [hourlyBudget, setHourlyBudget] = useState('');
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const qc = useQueryClient();
  const { data: existing, isLoading: isExistingLoading } = useQuery({
    queryKey: ['campaign-hourly-budget', profileId, campaign.campaignId],
    queryFn: async () => {
      const res = await campaignsApi.getCampaignHourlyBudget(profileId, campaign.campaignId, {
        product: campaign.adProduct,
      });
      if (!res.success) throw new Error('Failed to load hourly budget');
      return res.data as Record<string, unknown> | null;
    },
    enabled: isOpen && !!profileId && !!campaign.campaignId,
  });
  const uniformHourly = uniformWeeklyHourlyEdit(
    existing?.day_settings,
    existing?.hourly_budget,
    existing?.base_budget
  );
  const canAmountOnly = existing == null || uniformHourly.uniform;
  const detailsHref = `/accounts/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaign.campaignId)}?product=${encodeURIComponent(campaign.adProduct)}`;

  useEffect(() => {
    if (!isOpen || existing == null) return;
    if (existing.hourly_budget != null && existing.hourly_budget !== '') {
      setHourlyBudget(String(existing.hourly_budget));
    }
    if (typeof existing.active === 'boolean') setActive(existing.active);
  }, [isOpen, existing]);

  if (!isOpen) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAmountOnly) return;
    const n = parseFloat(hourlyBudget);
    if (Number.isNaN(n) || n <= 0) return;
    setSubmitting(true);
    try {
      const daySettings = uniformHourly.uniform
        ? uniformHourly.settings.map((row) => ({ ...row, hourly_budget: n }))
        : undefined;
      await campaignsApi.updateCampaignHourlyBudget(
        profileId,
        campaign.campaignId,
        {
          hourly_budget: n,
          active,
          schedule_mode: 'weekly',
          day_settings: daySettings,
        },
        { product: campaign.adProduct }
      );
      await qc.invalidateQueries({ queryKey: ['campaign-hourly-budget', profileId, campaign.campaignId] });
      await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl max-w-md w-full p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Hourly budget</h3>
          <button type="button" onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isExistingLoading ? (
          <p className="text-sm text-foreground-muted">Loading hourly budget…</p>
        ) : !canAmountOnly ? (
          <div>
            <p className="text-sm text-foreground-muted mb-4">
              This campaign uses different hourly amounts by day. Edit hourly and base amounts on the
              campaign details page.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm">
                Cancel
              </button>
              <Link
                to={detailsHref}
                className="flex-1 bg-brand text-white rounded-lg py-2 text-sm font-medium text-center"
                onClick={onClose}
              >
                Open details
              </Link>
            </div>
          </div>
        ) : (
        <form onSubmit={submit}>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Hourly budget (₹)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={hourlyBudget}
            onChange={(e) => setHourlyBudget(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2 mb-3"
            disabled={submitting}
          />
          <label className="flex items-center gap-2 mb-4 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={submitting}
            />
            Active
          </label>
          {campaign.adProduct !== 'SP' && (
            <p className="text-xs text-foreground-subtle mb-4">
              Configure multiplier time windows on the campaign details page.
            </p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm" disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-brand text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50"
              disabled={submitting}
            >
              Save
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

const StatusBadge = ({ state }: { state: string }) => {
  const u = state.toUpperCase();
  const cfg =
    u === 'ENABLED'
      ? { icon: Play, cls: 'bg-success-subtle text-success-ink border-success/20' }
      : u === 'PAUSED'
        ? { icon: Pause, cls: 'bg-warning-subtle text-warning-ink border-warning/20' }
        : { icon: Archive, cls: 'bg-page text-foreground-muted border-border' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {state}
    </span>
  );
};

export function SpCampaignsPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const location = useLocation();
  const { showToast } = useToast();
  const [hourlyCampaign, setHourlyCampaign] = useState<SpCampaignNormalized | null>(null);
  const [selectedCampaignKeys, setSelectedCampaignKeys] = useState<Set<string>>(new Set());
  const [bulkBudgetMode, setBulkBudgetMode] = useState<CampaignBudgetAdjustMode>('INCREASE_AMOUNT');
  const [bulkBudgetValue, setBulkBudgetValue] = useState('');
  const [bulkBudgetPreviewRows, setBulkBudgetPreviewRows] = useState<
    BulkCampaignBudgetPreviewRow[] | null
  >(null);
  const [bulkBudgetApplying, setBulkBudgetApplying] = useState(false);
  const activeTab = useMemo<ActiveTab>(() => parseActiveTabFromPath(location.pathname), [location.pathname]);
  const [budgetRulesProduct, setBudgetRulesProduct] = useState<CampaignAdProduct>('SP');
  const budgetRulesApiProduct = campaignAdProductToBudgetRuleApiProduct(budgetRulesProduct);
  const [budgetRuleType, setBudgetRuleType] = useState<'EVENT_DAILY' | 'SCHEDULE_WEEKLY' | 'PERFORMANCE_DAILY'>(
    'EVENT_DAILY'
  );
  const [ruleName, setRuleName] = useState('');
  const [increasePct, setIncreasePct] = useState('25');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [eventId, setEventId] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(['MONDAY']);
  const [threshold, setThreshold] = useState('20');
  const [comparisonOperator, setComparisonOperator] = useState('LESS_THAN_OR_EQUAL_TO');
  const [creatingBudgetRule, setCreatingBudgetRule] = useState(false);
  const [budgetRuleError, setBudgetRuleError] = useState<string | null>(null);
  const [editingBudgetRule, setEditingBudgetRule] = useState<Record<string, unknown> | null>(null);
  const [togglingBudgetRuleId, setTogglingBudgetRuleId] = useState<string | null>(null);
  const [createBudgetRuleCampaignIds, setCreateBudgetRuleCampaignIds] = useState<string[]>([]);
  const [createCampaignSearch, setCreateCampaignSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('ACTIVE');
  const [adProductFilter, setAdProductFilter] = useState<CampaignAdProductFilter>('ALL');
  const [portfolioFilter, setPortfolioFilter] = useState<CampaignPortfolioFilter>('ALL');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [debouncedCampaignSearch, setDebouncedCampaignSearch] = useState('');
  const [adGroupCampaignFilter, setAdGroupCampaignFilter] = useState('ALL');
  const [adGroupStateFilter, setAdGroupStateFilter] = useState<AdGroupStateFilter>('ENABLED');
  const [adGroupNameFilter, setAdGroupNameFilter] = useState('');
  const [keywordStateFilter, setKeywordStateFilter] = useState<KeywordStateFilter>('ENABLED');
  const [keywordCampaignFilter, setKeywordCampaignFilter] = useState('ALL');
  const [keywordNextToken, setKeywordNextToken] = useState<string | null>(null);
  const [targetCampaignFilter, setTargetCampaignFilter] = useState('ALL');
  const [targetStateFilter, setTargetStateFilter] = useState<TargetStateFilter>('ENABLED');
  const [targetNextToken, setTargetNextToken] = useState<string | null>(null);
  const [createCampaignPickerOpen, setCreateCampaignPickerOpen] = useState(false);
  const [reportBatchId, setReportBatchId] = useState<string | null>(null);
  const [reportActionError, setReportActionError] = useState<string | null>(null);
  const [reportActionBusy, setReportActionBusy] = useState<'last-two-months' | 'current-month' | null>(null);
  const [reportStartDate, setReportStartDate] = useState(() => getDefaultReportDateRange().startDate);
  const [reportEndDate, setReportEndDate] = useState(() => getDefaultReportDateRange().endDate);
  const defaultDashboardRange = getDefaultDashboardDateRange();
  const [dashboardStartDate] = useState(defaultDashboardRange.startDate);
  const [dashboardEndDate] = useState(defaultDashboardRange.endDate);
  const [reportAdProductFilters, setReportAdProductFilters] = useState<string[]>(() =>
    REPORT_AD_PRODUCT_OPTIONS.map((o) => o.id)
  );
  const [campaignReportColumns, setCampaignReportColumns] = useState<CampaignReportColumnId[]>(
    () => [...DEFAULT_CAMPAIGN_REPORT_COLUMN_IDS]
  );
  const [keywordReportColumns, setKeywordReportColumns] = useState<KeywordReportColumnId[]>(
    () => [...DEFAULT_KEYWORD_REPORT_COLUMN_IDS]
  );
  const [targetReportColumns, setTargetReportColumns] = useState<TargetReportColumnId[]>(
    () => [...DEFAULT_TARGET_REPORT_COLUMN_IDS]
  );
  const reportAdProductsParam = useMemo(
    () => reportAdProductsQueryParam(reportAdProductFilters),
    [reportAdProductFilters]
  );
  const keywordReportAdProductsParam = useMemo(() => {
    const selected = reportAdProductFilters.filter((id) =>
      (KEYWORD_REPORT_AD_PRODUCT_IDS as readonly string[]).includes(id)
    );
    return reportAdProductsQueryParam(
      selected.length > 0 ? selected : [...KEYWORD_REPORT_AD_PRODUCT_IDS]
    );
  }, [reportAdProductFilters]);
  const keywordReportAdProductFiltersActive = useMemo(
    () =>
      reportAdProductFilters.filter((id) =>
        (KEYWORD_REPORT_AD_PRODUCT_IDS as readonly string[]).includes(id)
      ),
    [reportAdProductFilters]
  );
  const targetReportAdProductsParam = useMemo(() => {
    const selected = reportAdProductFilters.filter((id) =>
      (TARGET_REPORT_AD_PRODUCT_IDS as readonly string[]).includes(id)
    );
    return reportAdProductsQueryParam(
      selected.length > 0 ? selected : [...TARGET_REPORT_AD_PRODUCT_IDS]
    );
  }, [reportAdProductFilters]);
  const targetReportAdProductFiltersActive = useMemo(
    () =>
      reportAdProductFilters.filter((id) =>
        (TARGET_REPORT_AD_PRODUCT_IDS as readonly string[]).includes(id)
      ),
    [reportAdProductFilters]
  );
  const dayOptions = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const accountName =
    (location.state as { accountName?: string } | null)?.accountName?.trim() || null;
  const qc = useQueryClient();
  const isReportRangeInvalid = reportEndDate < reportStartDate;
  const isDashboardRangeInvalid = dashboardEndDate < dashboardStartDate;

  const isDashboardTab = activeTab === 'dashboard';

  const dashboardScheduleFilter = useMemo((): CampaignScheduleFilterParams | null => {
    if (!isDashboardTab || isDashboardRangeInvalid) return null;
    return { scheduleStartDate: dashboardStartDate, scheduleEndDate: dashboardEndDate };
  }, [isDashboardTab, isDashboardRangeInvalid, dashboardStartDate, dashboardEndDate]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCampaignSearch(campaignSearch), 300);
    return () => window.clearTimeout(timer);
  }, [campaignSearch]);

  const campaignListFilters = useMemo((): ProfileCampaignListFilters | null => {
    if (!isDashboardTab && activeTab !== 'campaigns') return null;
    return {
      adProduct: adProductFilter,
      status: statusFilter,
      search: debouncedCampaignSearch,
      portfolioId: portfolioFilter,
    };
  }, [
    isDashboardTab,
    activeTab,
    adProductFilter,
    statusFilter,
    debouncedCampaignSearch,
    portfolioFilter,
  ]);

  const normalizedAdGroupNameFilter = adGroupNameFilter.trim();

  const dashboardAdProductsParam = useMemo(() => {
    if (adProductFilter === 'ALL') {
      return reportAdProductsQueryParam(REPORT_AD_PRODUCT_OPTIONS.map((o) => o.id));
    }
    const id =
      adProductFilter === 'SB'
        ? 'sponsored_brands'
        : adProductFilter === 'SD'
          ? 'sponsored_display'
          : 'sponsored_products';
    return reportAdProductsQueryParam([id]);
  }, [adProductFilter]);

  const dashboardSequential = useDashboardSequentialLoad(
    {
      profileId,
      schedule: dashboardScheduleFilter,
      dashboardStartDate,
      dashboardEndDate,
      dashboardAdProductsParam,
      adGroupCampaignFilter,
      adGroupStateFilter,
      normalizedAdGroupNameFilter,
      keywordStateFilter,
      keywordNextToken,
      targetCampaignFilter,
      targetStateFilter,
      targetNextToken,
    },
    !!profileId && isDashboardTab && !isDashboardRangeInvalid
  );

  const dashboardReportLoading =
    isDashboardTab && dashboardSequential.isLoading && !dashboardPhaseReached(dashboardSequential.phase, 'keywords');

  const dashboardKeywordsLoading =
    isDashboardTab &&
    dashboardSequential.isLoading &&
    !dashboardPhaseReached(dashboardSequential.phase, 'keywords');
  const dashboardKeywordCampaignFilter =
    keywordCampaignFilter !== 'ALL' ? keywordCampaignFilter : null;
  // const dashboardTargetsLoading =
  //   isDashboardTab && dashboardSequential.isLoading && dashboardSequential.phase !== 'complete';

  const { data: dashboardReportRowsData } = useQuery({
    queryKey: [
      'dashboard-report-rows',
      profileId,
      dashboardStartDate,
      dashboardEndDate,
      dashboardAdProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getSpReportRows(profileId, {
        startDate: dashboardStartDate,
        endDate: dashboardEndDate,
        adProducts: dashboardAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load campaign report metrics');
      return res.data;
    },
    enabled: false,
  });

  const dashboardMetricsByCampaign = useMemo(() => {
    const parsed = parseReportRowsPayload(dashboardReportRowsData);
    const map = new Map<string, ReportRow>();
    for (const row of parsed.rows) {
      const campaignId = row.campaignId ? String(row.campaignId) : '';
      const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
      if (!campaignId) continue;
      map.set(`${adProduct}::${campaignId}`, row);
    }
    return map;
  }, [dashboardReportRowsData]);

  const {
    data: dashboardKeywordReportRowsData,
    isLoading: isDashboardKeywordReportLoading,
    isFetching: isDashboardKeywordReportFetching,
    error: dashboardKeywordReportError,
    refetch: refetchDashboardKeywordReport,
  } = useQuery({
    queryKey: [
      'dashboard-keyword-report-rows',
      profileId,
      dashboardStartDate,
      dashboardEndDate,
      dashboardKeywordCampaignFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.getKeywordReportRows(profileId, {
        startDate: dashboardStartDate,
        endDate: dashboardEndDate,
        adProducts: keywordReportAdProductsQueryParam(),
        ...(dashboardKeywordCampaignFilter ? { campaignId: dashboardKeywordCampaignFilter } : {}),
      });
      if (!res.success) throw new Error('Failed to load keyword report metrics');
      return res.data;
    },
    enabled: isDashboardTab && !!profileId && !isDashboardRangeInvalid,
  });

  const dashboardMetricsByKeyword = useMemo(() => {
    const parsed = parseReportRowsPayload(dashboardKeywordReportRowsData);
    return buildKeywordMetricsMap(parsed.rows);
  }, [dashboardKeywordReportRowsData]);

  const {
    data: profileCampaignsData,
    isLoading,
    isFetching: isCampaignsFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['profile-campaigns', profileId, dashboardScheduleFilter, campaignListFilters],
    queryFn: async () => {
      const res = await adsApi.fetchProfileCampaigns(
        profileId,
        dashboardScheduleFilter,
        campaignListFilters ?? undefined
      );
      if (!res.success) throw new Error('Failed to load campaigns');
      return res.data;
    },
    enabled: !!profileId && tabNeedsProfileCampaigns(activeTab),
    placeholderData: keepPreviousData,
  });

  const { data: portfoliosData } = useQuery({
    queryKey: ['profile-portfolios', profileId],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success || !res.data) throw new Error('Failed to load portfolios');
      return res.data;
    },
    enabled: !!profileId && (isDashboardTab || activeTab === 'campaigns'),
    staleTime: 60_000,
  });

  const portfolioOptions = useMemo(() => {
    const portfolios = (portfoliosData?.portfolios ?? []) as Portfolio[];
    return [...portfolios].sort((a, b) => a.name.localeCompare(b.name));
  }, [portfoliosData]);

  const portfolioFilterLabel = useMemo(() => {
    if (portfolioFilter === 'ALL') return null;
    if (portfolioFilter === 'NONE') return 'no portfolio';
    return portfolioOptions.find((p) => p.portfolioId === portfolioFilter)?.name ?? portfolioFilter;
  }, [portfolioFilter, portfolioOptions]);

  const campaigns = useMemo(() => parseCampaignsPayload(profileCampaignsData), [profileCampaignsData]);

  const filteredCampaigns = campaigns;

  const refreshCampaignsAfterBudgetUpdate = useCallback(() => {
    void refetch();
    if (isDashboardTab) void dashboardSequential.refetch();
  }, [isDashboardTab, refetch, dashboardSequential]);

  const toggleCampaignSelection = useCallback((key: string) => {
    setSelectedCampaignKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAllFilteredCampaigns = useCallback(() => {
    setSelectedCampaignKeys(
      new Set(filteredCampaigns.map((campaign) => campaignSelectionKey(campaign)))
    );
  }, [filteredCampaigns]);

  const clearCampaignSelection = useCallback(() => {
    setSelectedCampaignKeys(new Set());
  }, []);

  const allFilteredCampaignsSelected =
    filteredCampaigns.length > 0 &&
    filteredCampaigns.every((campaign) =>
      selectedCampaignKeys.has(campaignSelectionKey(campaign))
    );

  const handleOpenBulkBudgetConfirm = useCallback(() => {
    const value = Number(bulkBudgetValue);
    if (!Number.isFinite(value) || value <= 0) {
      showToast('Enter a valid adjustment value greater than 0', 'error');
      return;
    }
    if (bulkBudgetMode === 'DECREASE_PERCENT' && value > 100) {
      showToast('Decrease percentage cannot exceed 100%', 'error');
      return;
    }
    const selected = filteredCampaigns.filter((campaign) =>
      selectedCampaignKeys.has(campaignSelectionKey(campaign))
    );
    if (!selected.length) return;
    const rows: BulkCampaignBudgetPreviewRow[] = selected.map((campaign) => {
      const { budget, error } = computeAdjustedBudget(
        campaign.budget,
        bulkBudgetMode,
        value,
        campaign.adProduct,
        campaign.budgetType
      );
      return {
        campaign,
        newBudget: budget ?? 0,
        error,
      };
    });
    setBulkBudgetPreviewRows(rows);
  }, [bulkBudgetMode, bulkBudgetValue, filteredCampaigns, selectedCampaignKeys, showToast]);

  const handleConfirmBulkBudget = useCallback(async () => {
    if (!bulkBudgetPreviewRows) return;
    const updates = bulkBudgetPreviewRows
      .filter((row) => !row.error)
      .map((row) => ({
        campaignId: row.campaign.campaignId,
        adProduct: row.campaign.adProduct,
        budget: row.newBudget,
        budgetType: row.campaign.budgetType || 'DAILY',
        previousBudget: row.campaign.budget,
      }));
    if (!updates.length) return;
    setBulkBudgetApplying(true);
    try {
      const results = await applyBulkCampaignBudgetUpdates(profileId, updates);
      const failures = results.filter((item) => item.status === 'error');
      showToast(
        summarizeBulkBudgetResults(results),
        failures.length ? 'error' : 'success'
      );
      setBulkBudgetPreviewRows(null);
      setBulkBudgetValue('');
      clearCampaignSelection();
      refreshCampaignsAfterBudgetUpdate();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update campaign budgets';
      showToast(message, 'error');
    } finally {
      setBulkBudgetApplying(false);
    }
  }, [
    bulkBudgetPreviewRows,
    clearCampaignSelection,
    profileId,
    refreshCampaignsAfterBudgetUpdate,
    showToast,
  ]);

  const {
    data: adGroupsData,
    isLoading: isAdGroupsLoading,
    error: adGroupsError,
    refetch: refetchAdGroups,
  } = useQuery({
    queryKey: [
      'sb-ad-groups',
      profileId,
      adGroupCampaignFilter,
      adGroupStateFilter,
      normalizedAdGroupNameFilter,
      dashboardScheduleFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.listSbAdGroups(
        profileId,
        {
          include_extended_data_fields: true,
          campaign_ids: adGroupCampaignFilter === 'ALL' ? [] : [adGroupCampaignFilter],
          states:
            adGroupStateFilter === 'ALL'
              ? ['ENABLED', 'PAUSED', 'ARCHIVED']
              : [adGroupStateFilter],
          name_match_type: 'EXACT_MATCH',
          names: normalizedAdGroupNameFilter ? [normalizedAdGroupNameFilter] : [],
          max_results: 200,
        },
        dashboardScheduleFilter
      );
      if (!res.success) throw new Error('Failed to load ad groups');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'ad-groups',
  });
  const {
    data: spAdGroupsData,
    isLoading: isSpAdGroupsLoading,
    error: spAdGroupsError,
    refetch: refetchSpAdGroups,
  } = useQuery({
    queryKey: [
      'sp-ad-groups',
      profileId,
      adGroupCampaignFilter,
      adGroupStateFilter,
      normalizedAdGroupNameFilter,
      dashboardScheduleFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.listSpAdGroups(
        profileId,
        {
          include_extended_data_fields: true,
          campaign_ids: adGroupCampaignFilter === 'ALL' ? [] : [adGroupCampaignFilter],
          ad_group_ids: [],
          states:
            adGroupStateFilter === 'ALL'
              ? ['ENABLED', 'PAUSED', 'ARCHIVED']
              : [adGroupStateFilter],
          max_results: 500,
        },
        dashboardScheduleFilter
      );
      if (!res.success) throw new Error('Failed to load SP ad groups');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'ad-groups',
  });
  const {
    data: sdAdGroupsData,
    isLoading: isSdAdGroupsLoading,
    error: sdAdGroupsError,
    refetch: refetchSdAdGroups,
  } = useQuery({
    queryKey: ['sd-ad-groups', profileId, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.getSdAdGroups(profileId, dashboardScheduleFilter);
      if (!res.success) throw new Error('Failed to load SD ad groups');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'ad-groups',
  });
  const adGroupsPayload = useMemo(() => parseAdGroupsPayload(adGroupsData), [adGroupsData]);
  const spAdGroupsPayload = useMemo(() => parseSpAdGroupsPayload(spAdGroupsData), [spAdGroupsData]);
  const sdAdGroupsPayload = useMemo(() => parseSdAdGroupsPayload(sdAdGroupsData), [sdAdGroupsData]);
  const campaignNameById = useMemo(
    () => new Map(campaigns.map((campaign) => [campaign.campaignId, campaign.name])),
    [campaigns]
  );
  const {
    data: keywordsData,
    isLoading: isKeywordsLoading,
    error: keywordsError,
    refetch: refetchKeywords,
  } = useQuery({
    queryKey: ['sp-keywords', profileId, keywordStateFilter, keywordNextToken, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.listSpKeywords(
        profileId,
        {
          include_extended_data_fields: true,
          states: keywordStateFilter === 'ALL' ? [] : [keywordStateFilter],
          max_results: 200,
          next_token: keywordNextToken,
        },
        dashboardScheduleFilter
      );
      if (!res.success) throw new Error('Failed to load keywords');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'keywords',
  });
  const {
    data: sbKeywordsData,
    isLoading: isSbKeywordsLoading,
    error: sbKeywordsError,
    refetch: refetchSbKeywords,
  } = useQuery({
    queryKey: ['sb-keywords', profileId, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.getSbKeywords(profileId, { count: 500 }, dashboardScheduleFilter);
      if (!res.success) throw new Error('Failed to load SB keywords');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'keywords',
  });
  const keywordsPayload = useMemo(() => parseKeywordsPayload(keywordsData), [keywordsData]);
  const {
    data: targetsData,
    isLoading: isTargetsLoading,
    error: targetsError,
    refetch: refetchTargets,
  } = useQuery({
    queryKey: [
      'sp-targets',
      profileId,
      targetCampaignFilter,
      targetStateFilter,
      targetNextToken,
      dashboardScheduleFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.listSpTargets(
        profileId,
        {
          campaign_ids: targetCampaignFilter === 'ALL' ? [] : [targetCampaignFilter],
          states: targetStateFilter === 'ALL' ? [] : [targetStateFilter],
          max_results: 200,
          next_token: targetNextToken,
        },
        dashboardScheduleFilter
      );
      if (!res.success) throw new Error('Failed to load targets');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'targets',
  });
  const {
    data: sbTargetsData,
    isLoading: isSbTargetsLoading,
    error: sbTargetsError,
    refetch: refetchSbTargets,
  } = useQuery({
    queryKey: ['sb-targets', profileId, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.listSbTargets(profileId, { max_results: 500 }, dashboardScheduleFilter);
      if (!res.success) throw new Error('Failed to load SB targets');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'targets',
  });
  const {
    data: sdTargetsData,
    isLoading: isSdTargetsLoading,
    error: sdTargetsError,
    refetch: refetchSdTargets,
  } = useQuery({
    queryKey: ['sd-targets', profileId, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.getSdTargets(profileId, dashboardScheduleFilter);
      if (!res.success) throw new Error('Failed to load SD targets');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'targets',
  });
  const {
    data: sdProductAdsData,
    isLoading: isSdProductAdsLoading,
    error: sdProductAdsError,
    refetch: refetchSdProductAds,
  } = useQuery({
    queryKey: ['sd-product-ads', profileId, dashboardScheduleFilter],
    queryFn: async () => {
      const res = await adsApi.getSdProductAds(profileId, dashboardScheduleFilter);
      if (!res.success) throw new Error('Failed to load SD product ads');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'targets',
  });
  const {
    data: sdTargetReportRowsData,
    isLoading: isSdTargetReportRowsLoading,
    isFetching: isSdTargetReportRowsFetching,
    error: sdTargetReportRowsError,
    refetch: refetchSdTargetReportRows,
  } = useQuery({
    queryKey: [
      'sd-target-report-rows',
      profileId,
      dashboardStartDate,
      dashboardEndDate,
      targetCampaignFilter,
    ],
    queryFn: async () => {
      const res = await adsApi.getTargetReportRows(profileId, {
        startDate: dashboardStartDate,
        endDate: dashboardEndDate,
        adProducts: targetReportAdProductsQueryParam(),
        ...(targetCampaignFilter !== 'ALL' ? { campaignId: targetCampaignFilter } : {}),
      });
      if (!res.success) throw new Error('Failed to load SD target report metrics');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'targets' && !isDashboardRangeInvalid,
  });
  const targetsPayload = useMemo(() => parseSpTargetsPayload(targetsData), [targetsData]);
  const sbKeywordsPayload = useMemo(() => parseKeywordsPayload(sbKeywordsData), [sbKeywordsData]);
  const sbTargetsPayload = useMemo(() => parseSpTargetsPayload(sbTargetsData), [sbTargetsData]);
  const sdTargetsPayload = useMemo(() => parseSdTargetsPayload(sdTargetsData), [sdTargetsData]);
  const sdProductAdsPayload = useMemo(
    () => parseSdProductAdsPayload(sdProductAdsData),
    [sdProductAdsData]
  );

  const sdAdGroupsFiltered = useMemo(() => {
    const rows = sdAdGroupsPayload.adGroups;
    if (adGroupCampaignFilter === 'ALL') return rows;
    return rows.filter((r) => r.campaign_id === adGroupCampaignFilter);
  }, [sdAdGroupsPayload, adGroupCampaignFilter]);

  const sbKeywordsFiltered = useMemo(() => {
    const kws = sbKeywordsPayload.keywords;
    if (keywordStateFilter === 'ALL') return kws;
    return kws.filter((k) => (k.state || '').toUpperCase() === keywordStateFilter);
  }, [sbKeywordsPayload, keywordStateFilter]);

  const unifiedKeywords = useMemo(
    () => mergeUnifiedKeywords(keywordsPayload.keywords, sbKeywordsFiltered),
    [keywordsPayload.keywords, sbKeywordsFiltered]
  );

  const keywordCampaignOptions = useMemo(
    () => buildKeywordCampaignFilterOptions(campaigns, unifiedKeywords, campaignNameById),
    [campaigns, unifiedKeywords, campaignNameById]
  );

  const unifiedKeywordsFiltered = useMemo(
    () => filterUnifiedKeywordsByCampaign(unifiedKeywords, keywordCampaignFilter),
    [unifiedKeywords, keywordCampaignFilter]
  );

  const selectedKeywordCampaignName = useMemo(() => {
    if (keywordCampaignFilter === 'ALL') return null;
    return (
      keywordCampaignOptions.find((option) => option.id === keywordCampaignFilter)?.name ??
      keywordCampaignFilter
    );
  }, [keywordCampaignFilter, keywordCampaignOptions]);

  useEffect(() => {
    setKeywordCampaignFilter('ALL');
  }, [profileId, dashboardStartDate, dashboardEndDate]);

  useEffect(() => {
    if (keywordCampaignFilter === 'ALL') return;
    const optionIds = new Set(keywordCampaignOptions.map((option) => option.id));
    if (!optionIds.has(keywordCampaignFilter)) {
      setKeywordCampaignFilter('ALL');
    }
  }, [keywordCampaignFilter, keywordCampaignOptions]);

  const sbTargetsFiltered = useMemo(() => {
    let rows = sbTargetsPayload.targets;
    if (targetCampaignFilter !== 'ALL') {
      rows = rows.filter((t) => t.campaign_id === targetCampaignFilter);
    }
    if (targetStateFilter !== 'ALL') {
      rows = rows.filter((t) => (t.state || '').toUpperCase() === targetStateFilter);
    }
    return rows;
  }, [sbTargetsPayload, targetCampaignFilter, targetStateFilter]);

  const sdTargetsFiltered = useMemo(() => {
    let rows = sdTargetsPayload.targets;
    if (targetCampaignFilter !== 'ALL') {
      rows = rows.filter((t) => t.campaign_id === targetCampaignFilter);
    }
    if (targetStateFilter !== 'ALL') {
      rows = rows.filter((t) => (t.state || '').toUpperCase() === targetStateFilter);
    }
    return rows;
  }, [sdTargetsPayload, targetCampaignFilter, targetStateFilter]);

  const sdTargetMetricsByTarget = useMemo(() => {
    const parsed = parseReportRowsPayload(sdTargetReportRowsData);
    return buildTargetMetricsMap(parsed.rows);
  }, [sdTargetReportRowsData]);

  const sdTargetMetricsLoading = isSdTargetReportRowsLoading || isSdTargetReportRowsFetching;

  const sdProductAdsFiltered = useMemo(() => {
    let rows = sdProductAdsPayload.productAds;
    if (targetCampaignFilter !== 'ALL') {
      rows = rows.filter((a) => a.campaign_id === targetCampaignFilter);
    }
    return rows;
  }, [sdProductAdsPayload, targetCampaignFilter]);

  const {
    data: budgetRulesData,
    isLoading: isBudgetRulesLoading,
    error: budgetRulesError,
    refetch: refetchBudgetRules,
  } = useQuery({
    queryKey: ['budget-rules', budgetRulesApiProduct, profileId],
    queryFn: async () => {
      const res = await adsApi.getBudgetRules(profileId, budgetRulesApiProduct, 30);
      if (!res.success) throw new Error('Failed to load budget rules');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'budget-rules',
  });

  const budgetRules = useMemo(() => extractBudgetRulesList(budgetRulesData), [budgetRulesData]);

  const budgetRulesNextToken = useMemo(() => getBudgetRulesNextToken(budgetRulesData), [budgetRulesData]);

  const budgetRulesProductCampaigns = useMemo(
    () => campaigns.filter((c) => c.adProduct === budgetRulesProduct),
    [campaigns, budgetRulesProduct]
  );

  const createFormCampaignOptions = useMemo(
    () => filterCampaignsBySearch(budgetRulesProductCampaigns, createCampaignSearch),
    [budgetRulesProductCampaigns, createCampaignSearch]
  );

  useEffect(() => {
    setCreateBudgetRuleCampaignIds([]);
    setCreateCampaignSearch('');
  }, [budgetRulesProduct]);

  const handleBudgetRuleAssociationsChanged = () => {
    void qc.invalidateQueries({
      queryKey: ['budget-rules', budgetRulesApiProduct, profileId],
    });
  };

  const toggleCreateBudgetRuleCampaign = (campaignId: string, checked: boolean) => {
    setCreateBudgetRuleCampaignIds((prev) => {
      if (checked) {
        return prev.includes(campaignId) ? prev : [...prev, campaignId];
      }
      return prev.filter((id) => id !== campaignId);
    });
  };

  const {
    data: reportSummaryData,
    isLoading: isReportSummaryLoading,
    error: reportSummaryError,
    refetch: refetchReportSummary,
  } = useQuery({
    queryKey: ['sp-report-summary', profileId, reportStartDate, reportEndDate, reportAdProductsParam],
    queryFn: async () => {
      const res = await adsApi.getSpReportSummary(profileId, {
        startDate: reportStartDate,
        endDate: reportEndDate,
        adProducts: reportAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load report summary');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'reports' && !isReportRangeInvalid,
  });
  const {
    data: reportRowsData,
    isLoading: isReportRowsLoading,
    error: reportRowsError,
    refetch: refetchReportRows,
  } = useQuery({
    queryKey: ['sp-report-rows', profileId, reportStartDate, reportEndDate, reportAdProductsParam],
    queryFn: async () => {
      const res = await adsApi.getSpReportRows(profileId, {
        startDate: reportStartDate,
        endDate: reportEndDate,
        adProducts: reportAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load report rows');
      return res.data;
    },
    enabled: !!profileId && activeTab === 'reports' && !isReportRangeInvalid,
  });
  const {
    data: keywordReportRowsData,
    isLoading: isKeywordReportRowsLoading,
    error: keywordReportRowsError,
    refetch: refetchKeywordReportRows,
  } = useQuery({
    queryKey: [
      'keyword-report-rows',
      profileId,
      reportStartDate,
      reportEndDate,
      keywordReportAdProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getKeywordReportRows(profileId, {
        startDate: reportStartDate,
        endDate: reportEndDate,
        adProducts: keywordReportAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load keyword report rows');
      return res.data;
    },
    enabled:
      !!profileId &&
      activeTab === 'reports' &&
      !isReportRangeInvalid &&
      keywordReportAdProductFiltersActive.length > 0,
  });
  const {
    data: targetReportRowsData,
    isLoading: isTargetReportRowsLoading,
    error: targetReportRowsError,
    refetch: refetchTargetReportRows,
  } = useQuery({
    queryKey: [
      'target-report-rows',
      profileId,
      reportStartDate,
      reportEndDate,
      targetReportAdProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getTargetReportRows(profileId, {
        startDate: reportStartDate,
        endDate: reportEndDate,
        adProducts: targetReportAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load target report rows');
      return res.data;
    },
    enabled:
      !!profileId &&
      activeTab === 'reports' &&
      !isReportRangeInvalid &&
      targetReportAdProductFiltersActive.length > 0,
  });
  const {
    data: reportBatchStatusData,
    isLoading: isReportBatchLoading,
    error: reportBatchError,
    refetch: refetchReportBatch,
  } = useQuery({
    queryKey: ['sp-report-batch', profileId, reportBatchId],
    queryFn: async () => {
      if (!reportBatchId) throw new Error('Missing report batch id');
      const res = await adsApi.getReportBatchStatus(profileId, reportBatchId);
      if (!res.success) throw new Error('Failed to load report batch status');
      return res.data;
    },
    enabled: !!profileId && !!reportBatchId && activeTab === 'reports',
    refetchInterval: (query) => {
      const payload = parseReportBatchPayload(query.state.data);
      const status = payload.batch?.status?.toLowerCase();
      return status && !['completed', 'failed', 'partial_success'].includes(status) ? 15000 : false;
    },
  });
  const reportSummary = useMemo(() => parseReportSummaryPayload(reportSummaryData), [reportSummaryData]);
  const reportRows = useMemo(() => parseReportRowsPayload(reportRowsData), [reportRowsData]);
  const keywordReportRows = useMemo(
    () => parseReportRowsPayload(keywordReportRowsData),
    [keywordReportRowsData]
  );
  const targetReportRows = useMemo(() => {
    const parsed = parseReportRowsPayload(targetReportRowsData);
    return {
      ...parsed,
      rows: parsed.rows.map((row) => {
        const campaignId = row.campaignId ? String(row.campaignId) : '';
        return {
          ...row,
          campaignName: campaignId ? campaignNameById.get(campaignId) : undefined,
        };
      }),
    };
  }, [targetReportRowsData, campaignNameById]);
  const visibleCampaignReportTableColumns = useMemo(
    () => visibleCampaignReportColumns(campaignReportColumns, reportAdProductFilters.length > 1),
    [campaignReportColumns, reportAdProductFilters.length]
  );
  const visibleKeywordReportTableColumns = useMemo(
    () =>
      visibleKeywordReportColumns(
        keywordReportColumns,
        keywordReportAdProductFiltersActive.length > 1
      ),
    [keywordReportColumns, keywordReportAdProductFiltersActive.length]
  );
  const visibleTargetReportTableColumns = useMemo(
    () => visibleTargetReportColumns(targetReportColumns),
    [targetReportColumns]
  );
  const reportBatch = useMemo(() => parseReportBatchPayload(reportBatchStatusData), [reportBatchStatusData]);

  const toggleReportAdProductFilter = (id: string) => {
    setReportAdProductFilters((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev;
      if (has) return prev.filter((x) => x !== id);
      return [...prev, id].sort();
    });
  };

  const submitBudgetRule = async (e: React.FormEvent) => {
    e.preventDefault();
    setBudgetRuleError(null);

    const { payload, error } = buildCreateBudgetRulePayload({
      kind: budgetRuleType as BudgetRuleKind,
      ruleName,
      defaultName: 'Budget rule',
      increasePct,
      startDate,
      endDate,
      eventId,
      daysOfWeek,
      threshold,
      comparisonOperator,
    });
    if (error || !payload) {
      setBudgetRuleError(error || 'Invalid form');
      return;
    }

    setCreatingBudgetRule(true);
    try {
      const res = await adsApi.createBudgetRules(profileId, budgetRulesApiProduct, {
        budgetRulesDetails: [payload],
        ...(createBudgetRuleCampaignIds.length > 0
          ? { campaignIds: createBudgetRuleCampaignIds }
          : {}),
      });
      if (!res.success) throw new Error('Failed to create budget rule');
      setRuleName('');
      setCreateBudgetRuleCampaignIds([]);
      setCreateCampaignSearch('');
      await qc.invalidateQueries({
        queryKey: ['budget-rules', budgetRulesApiProduct, profileId],
      });
    } catch (err) {
      setBudgetRuleError((err as Error).message || 'Failed to create budget rule');
    } finally {
      setCreatingBudgetRule(false);
    }
  };

  const toggleBudgetRuleState = async (row: Record<string, unknown>) => {
    const ruleId = typeof row.ruleId === 'string' ? row.ruleId : '';
    const current = typeof row.ruleState === 'string' ? row.ruleState.toUpperCase() : '';
    const nextState = current === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    const { payload, error } = buildStateOnlyUpdatePayload(row, nextState);
    if (error || !payload) {
      setBudgetRuleError(error || 'Cannot update rule state');
      return;
    }
    setTogglingBudgetRuleId(ruleId);
    setBudgetRuleError(null);
    try {
      const res = await adsApi.updateBudgetRules(profileId, budgetRulesApiProduct, {
        budgetRulesDetails: [payload],
      });
      if (!res.success) throw new Error('Failed to update budget rule state');
      await qc.invalidateQueries({
        queryKey: ['budget-rules', budgetRulesApiProduct, profileId],
      });
    } catch (err) {
      setBudgetRuleError((err as Error).message || 'Failed to update budget rule state');
    } finally {
      setTogglingBudgetRuleId(null);
    }
  };

  const startReportSync = async (mode: 'last-two-months' | 'current-month') => {
    setReportActionBusy(mode);
    setReportActionError(null);
    try {
      const body = { accountName: accountName ?? undefined };
      const res =
        mode === 'last-two-months'
          ? await adsApi.startSpLastTwoMonthsReport(profileId, body)
          : await adsApi.startSpCurrentMonthReport(profileId, body);
      if (!res.success) throw new Error('Failed to start report sync');
      const payload = parseReportBatchPayload(res.data);
      if (payload.batch?.id) {
        setReportBatchId(payload.batch.id);
      }
      await qc.invalidateQueries({ queryKey: ['sp-report-summary', profileId] });
      await qc.invalidateQueries({ queryKey: ['sp-report-rows', profileId] });
      await qc.invalidateQueries({ queryKey: ['sp-report-batch', profileId] });
    } catch (err) {
      setReportActionError((err as Error).message || 'Failed to start report sync');
    } finally {
      setReportActionBusy(null);
    }
  };

  if (!profileId) {
    return <div className="p-8">Missing profile id.</div>;
  }

  if (tabNeedsProfileCampaigns(activeTab) && isLoading && !profileCampaignsData) {
    return <LoadingScreen />;
  }

  if (isDashboardTab && dashboardSequential.error) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <p className="text-error mb-4">{(dashboardSequential.error as Error).message}</p>
        <button
          type="button"
          onClick={() => void dashboardSequential.refetch()}
          className="text-brand underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <p className="text-error mb-4">{(error as Error).message}</p>
        <button type="button" onClick={() => refetch()} className="text-brand underline">
          Retry
        </button>
      </div>
    );
  }

  const dashboardTableSizing = (compactVertical: boolean) =>
    compactVertical
      ? {
          scrollWrap: 'max-h-96 overflow-auto custom-scrollbar',
          table: 'min-w-full text-body-sm',
          th: 'px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted',
          td: 'px-4 py-3',
          thead: 'bg-surface-muted border-b border-border sticky top-0 z-10',
        }
      : {
          scrollWrap: 'overflow-x-auto custom-scrollbar',
          table: 'min-w-full text-sm',
          th: 'px-4 py-3 text-xs font-bold uppercase tracking-wide text-foreground-muted',
          td: 'px-4 py-3',
          thead: 'bg-surface-muted border-b border-border',
        };

  const campaignsPanel = (compactVertical: boolean) => {
    const tc = dashboardTableSizing(compactVertical);
    const toolbar = (
      <div
        className={
          compactVertical
            ? 'mb-4 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm'
            : 'mb-6 flex flex-wrap justify-between items-center gap-3'
        }
      >
        <div className="flex flex-wrap items-center gap-3 min-w-0 flex-1">
          <p className={compactVertical ? 'sr-only' : 'text-foreground-muted shrink-0 text-body-sm'}>
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
            {adProductFilter !== 'ALL' ? ` (${campaignAdProductLabel(adProductFilter)})` : ''}
            {statusFilter !== 'ALL' ? ` · ${statusFilter.toLowerCase()}` : ''}
            {portfolioFilterLabel ? ` · ${portfolioFilterLabel}` : ''}
          </p>
          <div className="w-full sm:w-auto sm:min-w-[220px] sm:max-w-md flex-1">
            <label htmlFor="campaign-search" className="sr-only">
              Search campaigns
            </label>
            <input
              id="campaign-search"
              type="search"
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              placeholder={CAMPAIGN_SEARCH_PLACEHOLDER}
              autoComplete="off"
              className="w-full rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {compactVertical ? (
            <span className="text-xs font-bold uppercase text-foreground-muted">Filter by:</span>
          ) : null}
          <label htmlFor="campaign-ad-product-filter" className={compactVertical ? 'sr-only' : 'text-body-sm text-foreground-muted'}>
            Ad product
          </label>
          <select
            id="campaign-ad-product-filter"
            value={adProductFilter}
            onChange={(e) => setAdProductFilter(e.target.value as CampaignAdProductFilter)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            <option value="ALL">All</option>
            <option value="SP">{campaignAdProductLabel('SP')}</option>
            <option value="SB">{campaignAdProductLabel('SB')}</option>
            <option value="SD">{campaignAdProductLabel('SD')}</option>
          </select>
          <label htmlFor="campaign-status-filter" className={compactVertical ? 'sr-only' : 'text-body-sm text-foreground-muted'}>
            Status
          </label>
          <select
            id="campaign-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CampaignStatusFilter)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          >
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="ARCHIVED">Archived</option>
          </select>
          <label htmlFor="campaign-portfolio-filter" className={compactVertical ? 'sr-only' : 'text-body-sm text-foreground-muted'}>
            Portfolio
          </label>
          <select
            id="campaign-portfolio-filter"
            value={portfolioFilter}
            onChange={(e) => setPortfolioFilter(e.target.value as CampaignPortfolioFilter)}
            className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30 max-w-[220px]"
          >
            <option value="ALL">All portfolios</option>
            <option value="NONE">No portfolio</option>
            {portfolioOptions.map((portfolio) => (
              <option key={portfolio.portfolioId} value={portfolio.portfolioId}>
                {portfolio.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setCreateCampaignPickerOpen(true)}
            className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            New campaign
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isCampaignsFetching}
            className="rounded-lg border border-border px-3 py-2 text-body-sm text-foreground-muted hover:bg-surface-muted disabled:opacity-60"
          >
            {isCampaignsFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          {isCampaignsFetching ? (
            <span className="inline-flex items-center gap-1.5 text-body-sm text-foreground-muted">
              <Spinner className="w-3.5 h-3.5" />
              Updating…
            </span>
          ) : null}
        </div>
      </div>
    );

    const bulkBudgetToolbar =
      selectedCampaignKeys.size > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand/20 bg-brand/5 p-4">
          <span className="text-sm font-semibold text-foreground">
            {selectedCampaignKeys.size} campaign{selectedCampaignKeys.size === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            onClick={clearCampaignSelection}
            className="text-sm text-brand hover:underline"
          >
            Clear
          </button>
          <label htmlFor="bulk-budget-mode" className="sr-only">
            Budget adjustment
          </label>
          <select
            id="bulk-budget-mode"
            value={bulkBudgetMode}
            onChange={(event) =>
              setBulkBudgetMode(event.target.value as CampaignBudgetAdjustMode)
            }
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
            {CAMPAIGN_BUDGET_ADJUST_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {campaignBudgetAdjustModeLabel(mode)}
                </option>
              )
            )}
          </select>
          <input
            type="number"
            min="0"
            step={bulkBudgetMode.endsWith('PERCENT') ? '1' : '0.01'}
            value={bulkBudgetValue}
            onChange={(event) => setBulkBudgetValue(event.target.value)}
            placeholder={bulkBudgetMode.endsWith('PERCENT') ? 'Percent' : 'Amount (₹)'}
            className="w-32 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handleOpenBulkBudgetConfirm}
            disabled={!bulkBudgetValue.trim()}
            className="rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
          >
            Adjust budget
          </button>
        </div>
      ) : null;

    return (
    <>
      {toolbar}
      {bulkBudgetToolbar}
      <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <div className={tc.scrollWrap}>
          <table className={tc.table}>
            <thead className={tc.thead}>
              <tr>
                <th className={`${tc.th} w-10`}>
                  <input
                    type="checkbox"
                    checked={allFilteredCampaignsSelected}
                    onChange={(event) => {
                      if (event.target.checked) selectAllFilteredCampaigns();
                      else clearCampaignSelection();
                    }}
                    aria-label="Select all campaigns"
                  />
                </th>
                <th className={tc.th}>Campaign name</th>
                <th className={tc.th}>Ad product</th>
                <th className={tc.th}>Status</th>
                <th className={tc.th}>Targeting</th>
                <th className={tc.th}>Campaign start date</th>
                <th className={tc.th}>Campaign end date</th>
                <th className={tc.th}>Campaign budget amount</th>
                <th className={`${tc.th} text-right`}>Clicks</th>
                <th className={`${tc.th} text-right`}>CTR</th>
                <th className={`${tc.th} text-right`}>Total cost</th>
                <th className={`${tc.th} text-right`}>CPC</th>
                <th className={`${tc.th} text-right`}>Purchases</th>
                <th className={`${tc.th} text-right`}>Sales</th>
                <th className={`${tc.th} text-right`}>ROAS</th>
                <th className={`${tc.th} text-center`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredCampaigns.map((c) => {
                const campaignType =
                  String(
                    getRawValue(c.raw, ['campaignType', 'campaign_type', 'targetingType', 'targeting_type']) ?? '-'
                  ) || '-';
                const reportRow =
                  activeTab === 'dashboard'
                    ? dashboardMetricsByCampaign.get(
                        campaignReportLookupKey(c.adProduct, c.campaignId)
                      )
                    : undefined;
                const { clicks, ctr, totalCost, cpc, purchases, sales, roas } =
                  activeTab === 'dashboard'
                    ? metricsFromReportRow(reportRow)
                    : {
                        clicks: getMetric(c.raw, ['clicks', 'clicks14d', 'click_count']),
                        ctr: getMetric(c.raw, ['ctr', 'clickThroughRate', 'click_through_rate']),
                        totalCost: getMetric(c.raw, ['cost', 'spend', 'totalCost', 'total_cost']),
                        cpc: getMetric(c.raw, ['cpc', 'averageCpc', 'avg_cpc']),
                        purchases: getMetric(c.raw, [
                          'purchases',
                          'orders',
                          'attributedConversions14d',
                          'attributed_conversions_14d',
                        ]),
                        sales: getMetric(c.raw, [
                          'sales',
                          'attributedSales14d',
                          'revenue',
                          'attributed_sales_14d',
                        ]),
                        roas: getMetric(c.raw, ['roas', 'returnOnAdSpend', 'return_on_ad_spend']),
                      };

                return (
                  <tr
                    key={`${c.adProduct}-${c.campaignId}`}
                    className="group align-top transition-colors hover:bg-surface-muted/80"
                  >
                    <td className={tc.td}>
                      <input
                        type="checkbox"
                        checked={selectedCampaignKeys.has(campaignSelectionKey(c))}
                        onChange={() => toggleCampaignSelection(campaignSelectionKey(c))}
                        aria-label={`Select ${c.name}`}
                      />
                    </td>
                    <td className={`${tc.td} min-w-[220px]`}>
                      <Link
                        to={profileCampaignDetailPath(profileId, c.campaignId, c.adProduct)}
                        className="block text-left font-bold text-brand hover:underline"
                      >
                        {c.name}
                      </Link>
                      <span className="mt-0.5 block font-mono text-[10px] text-foreground-muted">
                        ID: {c.campaignId}
                      </span>
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-foreground-muted`}>
                      <span
                        className="inline-flex rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs font-medium text-foreground"
                        title={campaignAdProductLabel(c.adProduct)}
                      >
                        {campaignAdProductLabel(c.adProduct)}
                      </span>
                    </td>
                    <td className={tc.td}>
                      <StatusBadge state={c.state} />
                    </td>
                    <td className={`${tc.td} whitespace-nowrap`}>
                      <span className="inline-flex rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-foreground-muted">
                        {campaignType}
                      </span>
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-foreground-muted`}>
                      {formatDateValue(c.startDate ?? getRawValue(c.raw, ['startDate', 'start_date']))}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-foreground-muted`}>
                      {formatDateValue(c.endDate ?? getRawValue(c.raw, ['endDate', 'end_date']))}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap`}>
                      <div className="flex items-center gap-1 rounded group-hover:bg-brand-subtle/50">
                        <EditableCampaignBudgetCell
                          profileId={profileId}
                          displayValue={formatCurrency(c.budget)}
                          editableTarget={resolveEditableCampaignBudget(c)}
                          campaignLabel={c.name}
                          onUpdated={refreshCampaignsAfterBudgetUpdate}
                        />
                      </div>
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm`}>
                      {formatNumberValue(clicks, 0)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm`}>
                      {formatPercentValue(ctr)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm font-bold`}>
                      {formatCurrencyValue(totalCost)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm`}>
                      {formatCurrencyValue(cpc)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm`}>
                      {formatNumberValue(purchases, 0)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right font-mono text-body-sm font-bold`}>
                      {formatCurrencyValue(sales)}
                    </td>
                    <td className={`${tc.td} whitespace-nowrap text-right`}>
                      {roas !== null ? (
                        <span className="inline-flex rounded bg-brand/10 px-2 py-0.5 font-mono text-body-sm font-bold text-brand">
                          {formatNumberValue(roas)}
                        </span>
                      ) : (
                        <span className="font-mono text-body-sm text-foreground-muted">-</span>
                      )}
                    </td>
                    <td className={tc.td}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setHourlyCampaign(c)}
                          className="text-xs font-semibold text-brand hover:underline"
                          title="Hourly budget"
                        >
                          Hourly
                        </button>
                        <Link
                          to={profileCampaignDetailPath(profileId, c.campaignId, c.adProduct)}
                          className="rounded bg-brand/10 p-1 text-brand transition-colors hover:bg-brand/20"
                          title="View campaign"
                          aria-label="View campaign"
                        >
                          <Eye className={compactVertical ? 'h-4 w-4' : 'h-[18px] w-[18px]'} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredCampaigns.length === 0 && (
        <p className={`text-center text-foreground-subtle ${compactVertical ? 'py-6' : 'py-12'}`}>
          {campaigns.length === 0
            ? 'No campaigns returned for this profile.'
            : dashboardScheduleFilter
              ? 'No campaigns overlap the selected date range.'
              : 'No campaigns match the current filters.'}
        </p>
      )}
    </>
    );
  };

  const adGroupsPanel = (compactVertical: boolean) => {
    const tc = dashboardTableSizing(compactVertical);
    const adGroupsPanelLoading = isDashboardTab
      ? false // dashboard ad groups section disabled
      : isAdGroupsLoading || isSpAdGroupsLoading || isSdAdGroupsLoading;
    const refreshAllAdGroups = () => {
      void refetchAdGroups();
      void refetchSpAdGroups();
      void refetchSdAdGroups();
    };
    const renderAdGroupRows = (
      rows: Array<SbAdGroupRow | SpAdGroupRowUi | SdAdGroupRowUi>
    ) =>
      rows.map((adGroup, index) => {
        const adGroupId = adGroup.ad_group_id || `idx-${index}`;
        const campaignId = adGroup.campaign_id || '-';
        return (
          <tr key={adGroupId} className="hover:bg-page/60">
            <td className={`${tc.td} text-foreground`}>{adGroup.name || '-'}</td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{adGroup.ad_group_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>
              {campaignId !== '-' ? campaignNameById.get(campaignId) || '-' : '-'}
            </td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{campaignId}</td>
            <td className={`${tc.td} text-foreground-muted`}>{adGroup.state || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{adGroup.extended_data?.serving_status || '-'}</td>
          </tr>
        );
      });

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-foreground-muted">
            Sponsored Brands: {adGroupsPayload.totalResults} · Sponsored Products:{' '}
            {spAdGroupsPayload.totalResults} · Sponsored Display: {sdAdGroupsFiltered.length}
            {adGroupCampaignFilter !== 'ALL' ? ' (SD rows match selected campaign)' : ''}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-foreground-muted mb-1">Campaign</label>
              <select
                value={adGroupCampaignFilter}
                onChange={(e) => setAdGroupCampaignFilter(e.target.value)}
                className="text-sm px-3 py-2 border border-border rounded-lg bg-surface"
              >
                <option value="ALL">All campaigns</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.campaignId} value={campaign.campaignId}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1">State</label>
              <select
                value={adGroupStateFilter}
                onChange={(e) => setAdGroupStateFilter(e.target.value as AdGroupStateFilter)}
                className="text-sm px-3 py-2 border border-border rounded-lg bg-surface"
              >
                <option value="ALL">All</option>
                <option value="ENABLED">Enabled</option>
                <option value="PAUSED">Paused</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1">Ad group name (exact)</label>
              <input
                type="text"
                value={adGroupNameFilter}
                onChange={(e) => setAdGroupNameFilter(e.target.value)}
                className="text-sm px-3 py-2 border border-border rounded-lg bg-surface"
                placeholder="Optional exact name"
              />
            </div>
            <button
              type="button"
              onClick={refreshAllAdGroups}
              className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-surface"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Brands</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {adGroupsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SB ad groups...</div>
              ) : adGroupsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(adGroupsError as Error).message || 'Failed to load SB ad groups'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchAdGroups()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : adGroupsPayload.adGroups.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SB ad groups for campaigns overlapping the selected date range.'
                    : 'No SB ad groups for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Ad group name</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Campaign ID</th>
                        <th className={tc.th}>State</th>
                        <th className={tc.th}>Serving status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderAdGroupRows(adGroupsPayload.adGroups)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Products</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {adGroupsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SP ad groups...</div>
              ) : spAdGroupsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(spAdGroupsError as Error).message || 'Failed to load SP ad groups'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchSpAdGroups()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : spAdGroupsPayload.adGroups.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SP ad groups for campaigns overlapping the selected date range.'
                    : 'No SP ad groups for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Ad group name</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Campaign ID</th>
                        <th className={tc.th}>State</th>
                        <th className={tc.th}>Serving status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderAdGroupRows(spAdGroupsPayload.adGroups)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Display</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {adGroupsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SD ad groups...</div>
              ) : sdAdGroupsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(sdAdGroupsError as Error).message || 'Failed to load SD ad groups'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchSdAdGroups()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : sdAdGroupsFiltered.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SD ad groups for campaigns overlapping the selected date range.'
                    : `No SD ad groups${adGroupCampaignFilter !== 'ALL' ? ' for the selected campaign.' : '.'}`}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Ad group name</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Campaign ID</th>
                        <th className={tc.th}>State</th>
                        <th className={tc.th}>Serving status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderAdGroupRows(sdAdGroupsFiltered)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const KEYWORD_CAMPAIGN_COL_WIDTH = '24rem';

  const keywordsPanel = (compactVertical: boolean) => {
    const tc = dashboardTableSizing(compactVertical);
    const refreshAllKeywords = () => {
      if (isDashboardTab) {
        void dashboardSequential.refetch();
        void refetchDashboardKeywordReport();
        return;
      }
      void refetchKeywords();
      void refetchSbKeywords();
    };
    const isKeywordsPanelLoading = isDashboardTab
      ? dashboardKeywordsLoading
      : isKeywordsLoading || isSbKeywordsLoading;
    const keywordsPanelError = keywordsError || sbKeywordsError;
    const formatUnifiedBid = (bid: string) => {
      if (bid === '-' || bid.startsWith('Multiple')) return bid;
      const n = Number(bid);
      if (!Number.isNaN(n)) return formatCurrency(n);
      return bid;
    };

    const servingStatusDotClass = (status: string) => {
      const s = status.toLowerCase();
      if (
        s.includes('delivering') ||
        s.includes('serving') ||
        s.includes('eligible') ||
        s.includes('active')
      ) {
        return 'bg-success';
      }
      if (s.includes('out of budget') || s.includes('budget')) return 'bg-warning';
      if (s.includes('paused') || s.includes('archived') || s.includes('limited')) return 'bg-chrome-label';
      return 'bg-chrome-label';
    };

    const stateBadgeVariant = (state: string) => {
      const u = state.toUpperCase();
      if (u === 'ENABLED') return 'success';
      if (u === 'PAUSED') return 'warning';
      if (u === 'ARCHIVED') return 'archived';
      return 'neutral';
    };

    const showKeywordMetrics = isDashboardTab;
    const keywordMetricsLoading =
      showKeywordMetrics &&
      (isDashboardKeywordReportLoading || isDashboardKeywordReportFetching);
    const dashboardKeywordReportPayload = parseReportRowsPayload(dashboardKeywordReportRowsData);
    const dashboardKeywordMetricsUnavailable =
      showKeywordMetrics &&
      !keywordMetricsLoading &&
      (Boolean(dashboardKeywordReportError) ||
        (unifiedKeywordsFiltered.length > 0 && dashboardKeywordReportPayload.rowCount === 0));

    const renderUnifiedKeywordRow = (row: (typeof unifiedKeywordsFiltered)[number], index: number) => {
        const campaignNames = resolveUnifiedCampaignNames(row, campaignNameById);
        const bidDisplay = formatUnifiedBid(row.bid);
        const detailTitle =
          row.sources.length > 1
            ? row.sources
                .map(
                  (s) =>
                    `${s.adProduct}: ${s.keyword_id || '-'} · ${s.campaign_id || '-'} · ${s.state || '-'}`
                )
                .join('\n')
            : undefined;

        const keywordState = row.state || '-';
        const status = row.serving_status || '-';
        const { clicks, ctr, totalCost, cpc, purchases, sales, roas } = showKeywordMetrics
          ? metricsFromKeywordSources(row.sources, dashboardMetricsByKeyword)
          : metricsFromReportRow(undefined);

        return (
          <tr
            key={`${row.groupKey}-${index}`}
            className="group align-top transition-colors hover:bg-surface-muted/80"
            title={detailTitle}
          >
            <td className={`${tc.td} font-bold text-foreground`}>{row.keyword_text}</td>
            <td className={`${tc.td}`}>
              <Badge variant="neutral" className="rounded-full">{row.campaign_types}</Badge>
            </td>
            <td className={`${tc.td} text-foreground-muted`} title={row.match_type}>
              <span className="text-xs font-medium uppercase tracking-wider">{row.match_type_abbrev}</span>
            </td>
            <td className={`${tc.td} text-right font-mono text-body-sm text-foreground-muted`}>
              <EditableKeywordBidCell
                profileId={profileId}
                displayValue={bidDisplay}
                editableTarget={resolveEditableUnifiedKeywordBid(row)}
                keywordLabel={row.keyword_text}
                onUpdated={() => {
                  void refetchKeywords();
                  void refetchSbKeywords();
                  if (isDashboardTab) void dashboardSequential.refetch();
                }}
              />
            </td>
            <td
              className={`${tc.td} font-medium text-brand`}
              style={{ width: KEYWORD_CAMPAIGN_COL_WIDTH, minWidth: KEYWORD_CAMPAIGN_COL_WIDTH }}
            >
              <KeywordCampaignNamesCell names={campaignNames} />
            </td>
            {showKeywordMetrics ? (
              <>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatNumberValue(clicks)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatPercentValue(ctr)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatCurrencyValue(totalCost)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatCurrencyValue(cpc)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatNumberValue(purchases)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatCurrencyValue(sales)}
                </td>
                <td className={`${tc.td} text-right font-mono text-body-sm`}>
                  {keywordMetricsLoading ? '…' : formatNumberValue(roas)}
                </td>
              </>
            ) : null}
            <td className={`${tc.td}`}>
              <Badge
                variant={stateBadgeVariant(keywordState)}
                className="rounded-full uppercase tracking-wider font-bold text-[10px]"
              >
                {keywordState}
              </Badge>
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${servingStatusDotClass(status)}`} />
                <span className="text-xs">{status}</span>
              </div>
            </td>
          </tr>
        );
      };

    const keywordTableHeader = (
      <>
        <colgroup>
          <col />
          <col className="w-28" />
          <col className="w-16" />
          <col className="w-24" />
          <col style={{ width: KEYWORD_CAMPAIGN_COL_WIDTH, minWidth: KEYWORD_CAMPAIGN_COL_WIDTH }} />
          {isDashboardTab ? (
            <>
              <col className="w-20" />
              <col className="w-16" />
              <col className="w-24" />
              <col className="w-20" />
              <col className="w-24" />
              <col className="w-24" />
              <col className="w-20" />
            </>
          ) : null}
          <col className="w-24" />
          <col className="w-40" />
        </colgroup>
        <thead className={tc.thead}>
          <tr>
            <th className={tc.th}>Keyword</th>
            <th className={tc.th}>Campaign types</th>
            <th className={tc.th}>Match type</th>
            <th className={`${tc.th} text-right`}>Bid</th>
            <th
              className={tc.th}
              style={{ width: KEYWORD_CAMPAIGN_COL_WIDTH, minWidth: KEYWORD_CAMPAIGN_COL_WIDTH }}
            >
              Campaign
            </th>
            {isDashboardTab ? (
              <>
                <th className={`${tc.th} text-right`}>Clicks</th>
                <th className={`${tc.th} text-right`}>CTR</th>
                <th className={`${tc.th} text-right`}>Spend</th>
                <th className={`${tc.th} text-right`}>CPC</th>
                <th className={`${tc.th} text-right`}>Purchases</th>
                <th className={`${tc.th} text-right`}>Sales</th>
                <th className={`${tc.th} text-right`}>ROAS</th>
              </>
            ) : null}
            <th className={tc.th}>State</th>
            <th className={tc.th}>Serving status</th>
          </tr>
        </thead>
      </>
    );

    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-border bg-surface shadow-sm p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className={compactVertical ? 'sr-only' : 'min-w-0'}>
              {showKeywordMetrics ? (
                <p className="text-body-sm text-foreground-muted mb-1">
                  Performance: {formatDashboardDateRangeLabel(dashboardStartDate, dashboardEndDate)}
                </p>
              ) : null}
            <p className={compactVertical ? 'sr-only' : 'text-body-sm text-foreground-muted'}>
              Unified keywords: {unifiedKeywordsFiltered.length}
              {keywordCampaignFilter !== 'ALL' ? ` of ${unifiedKeywords.length}` : ''} · SP entities:{' '}
              {keywordsPayload.keywords.length} · SB entities: {sbKeywordsFiltered.length}
              {keywordStateFilter !== 'ALL' ? ' (SB rows match state filter)' : ''}
              {keywordCampaignFilter !== 'ALL' ? ' (filtered by campaign)' : ''}
            </p>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label
                  className={compactVertical ? 'sr-only' : 'block text-xs font-bold uppercase text-foreground-muted mb-1'}
                >
                  Campaign
                </label>
                <select
                  value={keywordCampaignFilter}
                  onChange={(e) => setKeywordCampaignFilter(e.target.value)}
                  disabled={keywordCampaignOptions.length === 0}
                  className="max-w-[min(20rem,70vw)] rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="ALL">All campaigns</option>
                  {keywordCampaignOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className={compactVertical ? 'sr-only' : 'block text-xs font-bold uppercase text-foreground-muted mb-1'}
                >
                  State
                </label>
                <select
                  value={keywordStateFilter}
                  onChange={(e) => {
                    setKeywordStateFilter(e.target.value as KeywordStateFilter);
                    setKeywordNextToken(null);
                  }}
                  className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="ALL">All</option>
                  <option value="ENABLED">Enabled</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>

              <button
                type="button"
                onClick={refreshAllKeywords}
                className="text-body-sm px-3 py-2 border border-border rounded-lg hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
          {isKeywordsPanelLoading ? (
            <div className="p-6 text-body-sm text-foreground-muted">Loading keywords...</div>
          ) : keywordsPanelError ? (
            <div className="p-6">
              <p className="text-body-sm text-error mb-3">
                {(keywordsPanelError as Error).message || 'Failed to load keywords'}
              </p>
              {keywordsError && sbKeywordsError ? (
                <p className="text-xs text-foreground-muted mb-3">Both SP and SB keyword requests failed.</p>
              ) : null}
              <button type="button" onClick={refreshAllKeywords} className="text-body-sm text-brand underline">
                Retry
              </button>
            </div>
          ) : unifiedKeywords.length === 0 ? (
            <div className="p-6 text-body-sm text-foreground-muted">
              {dashboardScheduleFilter
                ? 'No keywords for campaigns overlapping the selected date range.'
                : 'No keywords for current filters.'}
            </div>
          ) : unifiedKeywordsFiltered.length === 0 ? (
            <div className="p-6 text-body-sm text-foreground-muted">
              No keywords for{' '}
              <span className="font-medium text-foreground">{selectedKeywordCampaignName}</span> with the
              current filters.
              {keywordsPayload.nextToken ? (
                <span className="block mt-2 text-xs">
                  More SP keywords may be available on the next page.
                </span>
              ) : null}
            </div>
          ) : (
            <>
              {dashboardKeywordMetricsUnavailable ? (
                <div className="border-b border-border bg-warning-subtle px-4 py-2 text-xs text-warning-ink">
                  Metrics unavailable for this date range — sync keyword reports for performance data.
                </div>
              ) : null}
            <div className={`${compactVertical ? '' : tc.scrollWrap} overflow-x-auto`}>
              {compactVertical ? (
                <VirtualizedTable
                  rows={unifiedKeywordsFiltered}
                  renderRow={renderUnifiedKeywordRow}
                  header={keywordTableHeader}
                  maxHeight="24rem"
                  tableClassName={`${tc.table} w-max min-w-full table-fixed`}
                  wrapperClassName=""
                />
              ) : (
                <table className={`${tc.table} w-max min-w-full table-fixed`}>
                  {keywordTableHeader}
                  <tbody className="divide-y divide-border">
                    {unifiedKeywordsFiltered.map((row, index) => renderUnifiedKeywordRow(row, index))}
                  </tbody>
                </table>
              )}
            </div>
            </>
          )}

          <div className="flex items-center justify-between gap-4 bg-surface-muted border-t border-border px-4 py-3">
            <p className="text-body-sm text-foreground-muted">
              Showing {unifiedKeywordsFiltered.length} keyword
              {unifiedKeywordsFiltered.length !== 1 ? 's' : ''}
              {keywordCampaignFilter !== 'ALL' ? ` for ${selectedKeywordCampaignName}` : ''}
              {keywordCampaignFilter !== 'ALL' && unifiedKeywordsFiltered.length < unifiedKeywords.length
                ? ` (${unifiedKeywords.length} loaded before campaign filter)`
                : ''}
              {keywordsPayload.nextToken
                ? keywordCampaignFilter !== 'ALL'
                  ? ' · campaign filter applies to loaded keywords; more SP results available'
                  : ' · more SP results available'
                : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setKeywordNextToken(null)}
                disabled={!keywordNextToken || isKeywordsLoading}
                className="text-body-sm px-3 py-2 border border-border rounded-lg hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First page
              </button>
              <button
                type="button"
                onClick={() => setKeywordNextToken(keywordsPayload.nextToken)}
                disabled={!keywordsPayload.nextToken || isKeywordsLoading}
                className="text-body-sm px-3 py-2 rounded-lg bg-brand text-brand-foreground hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next page (SP)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const targetsPanel = (compactVertical: boolean) => {
    const tc = dashboardTableSizing(compactVertical);
    const targetsPanelLoading = isDashboardTab
      ? false // dashboard targets section disabled
      : isTargetsLoading || isSbTargetsLoading || isSdTargetsLoading || isSdProductAdsLoading;
    const sdTargetMetricsPanelLoading = sdTargetMetricsLoading;
    const refreshAllTargets = () => {
      void refetchTargets();
      void refetchSbTargets();
      void refetchSdTargets();
      void refetchSdProductAds();
      void refetchSdTargetReportRows();
    };
    const renderTargetRows = (
      targetList: SpTargetRow[],
      adProduct: 'SP' | 'SB',
      onUpdated: () => void
    ) =>
      targetList.map((target, index) => {
        const targetId = target.target_id || `idx-${index}`;
        const campaignId = target.campaign_id || '-';
        const bidDisplay = typeof target.bid === 'number' ? formatCurrency(target.bid) : '-';
        const editableTarget =
          adProduct === 'SP'
            ? resolveEditableSpTargetBid(target)
            : resolveEditableSbTargetBid(target);
        return (
          <tr key={targetId} className="hover:bg-page/60">
            <td className={`${tc.td} text-foreground`}>{formatTargetExpression(target.expression)}</td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{target.target_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{target.expression_type || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>
              <EditableTargetBidCell
                profileId={profileId}
                displayValue={bidDisplay}
                editableTarget={editableTarget}
                targetLabel={formatTargetExpression(target.expression)}
                onUpdated={onUpdated}
              />
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {campaignId !== '-' ? campaignNameById.get(campaignId) || '-' : '-'}
            </td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{target.ad_group_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{target.state || '-'}</td>
          </tr>
        );
      });

    const renderSdTargetRows = (targetList: SpTargetRow[]) =>
      targetList.map((target, index) => {
        const targetId = target.target_id || `idx-${index}`;
        const campaignId = target.campaign_id || '-';
        const metrics = targetMetricsForCatalogRow(target, sdTargetMetricsByTarget);
        const { clicks, ctr, totalCost, cpc, purchases, sales, roas } = metrics;
        return (
          <tr key={targetId} className="hover:bg-page/60">
            <td className={`${tc.td} text-foreground`}>{formatTargetExpression(target.expression)}</td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{target.target_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{target.expression_type || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>
              {typeof target.bid === 'number' ? formatCurrency(target.bid) : '-'}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {campaignId !== '-' ? campaignNameById.get(campaignId) || '-' : '-'}
            </td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{target.ad_group_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{target.state || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatNumberValue(clicks, 0)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatPercentValue(ctr)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatCurrencyValue(totalCost)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatCurrencyValue(cpc)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatNumberValue(purchases, 0)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatCurrencyValue(sales)}
            </td>
            <td className={`${tc.td} text-foreground-muted`}>
              {sdTargetMetricsPanelLoading ? '…' : formatNumberValue(roas, 2)}
            </td>
          </tr>
        );
      });

    const renderProductAdRows = (ads: SdProductAdRow[]) =>
      ads.map((ad, index) => {
        const id = ad.ad_id || `idx-${index}`;
        const campaignId = ad.campaign_id || '-';
        return (
          <tr key={id} className="hover:bg-page/60">
            <td className={`${tc.td} text-foreground`}>{ad.ad_name || '-'}</td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{ad.ad_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>
              {campaignId !== '-' ? campaignNameById.get(campaignId) || '-' : '-'}
            </td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{ad.ad_group_id || '-'}</td>
            <td className={`${tc.td} text-foreground-muted font-mono`}>{ad.asin || '-'}</td>
            <td className={`${tc.td} text-foreground-muted`}>{ad.state || '-'}</td>
          </tr>
        );
      });

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-foreground-muted">
            SP: {targetsPayload.totalResults} · SB: {sbTargetsFiltered.length} · SD:{' '}
            {sdTargetsFiltered.length} · SD product ads: {sdProductAdsFiltered.length}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-foreground-muted mb-1">Campaign</label>
              <select
                value={targetCampaignFilter}
                onChange={(e) => {
                  setTargetCampaignFilter(e.target.value);
                  setTargetNextToken(null);
                }}
                className="text-sm px-3 py-2 border border-border rounded-lg bg-surface"
              >
                <option value="ALL">All campaigns</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.campaignId} value={campaign.campaignId}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1">State</label>
              <select
                value={targetStateFilter}
                onChange={(e) => {
                  setTargetStateFilter(e.target.value as TargetStateFilter);
                  setTargetNextToken(null);
                }}
                className="text-sm px-3 py-2 border border-border rounded-lg bg-surface"
              >
                <option value="ALL">All</option>
                <option value="ENABLED">Enabled</option>
                <option value="PAUSED">Paused</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
            {targetNextToken && (
              <button
                type="button"
                onClick={() => setTargetNextToken(null)}
                className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-surface"
              >
                First page
              </button>
            )}
            <button
              type="button"
              onClick={refreshAllTargets}
              className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-surface"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Products</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {targetsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SP targets...</div>
              ) : targetsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(targetsError as Error).message || 'Failed to load SP targets'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchTargets()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : targetsPayload.targets.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SP targets for campaigns overlapping the selected date range.'
                    : 'No SP targets for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Expression</th>
                        <th className={tc.th}>Target ID</th>
                        <th className={tc.th}>Expression type</th>
                        <th className={tc.th}>Bid</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderTargetRows(targetsPayload.targets, 'SP', () => {
                        void refetchTargets();
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Brands</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {targetsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SB targets...</div>
              ) : sbTargetsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(sbTargetsError as Error).message || 'Failed to load SB targets'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchSbTargets()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : sbTargetsFiltered.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SB targets for campaigns overlapping the selected date range.'
                    : 'No SB targets for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Expression</th>
                        <th className={tc.th}>Target ID</th>
                        <th className={tc.th}>Expression type</th>
                        <th className={tc.th}>Bid</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderTargetRows(sbTargetsFiltered, 'SB', () => {
                        void refetchSbTargets();
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Display — targets</h3>
            {sdTargetReportRowsError && (
              <p className="text-sm text-error mb-2">{(sdTargetReportRowsError as Error).message}</p>
            )}
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {targetsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SD targets...</div>
              ) : sdTargetsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(sdTargetsError as Error).message || 'Failed to load SD targets'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchSdTargets()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : sdTargetsFiltered.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SD targets for campaigns overlapping the selected date range.'
                    : 'No SD targets for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Expression</th>
                        <th className={tc.th}>Target ID</th>
                        <th className={tc.th}>Expression type</th>
                        <th className={tc.th}>Bid</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>State</th>
                        <th className={tc.th}>Clicks</th>
                        <th className={tc.th}>CTR</th>
                        <th className={tc.th}>Spend</th>
                        <th className={tc.th}>CPC</th>
                        <th className={tc.th}>Purchases</th>
                        <th className={tc.th}>Sales</th>
                        <th className={tc.th}>ROAS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderSdTargetRows(sdTargetsFiltered)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Sponsored Display — product ads</h3>
            <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
              {targetsPanelLoading ? (
                <div className="p-6 text-sm text-foreground-subtle">Loading SD product ads...</div>
              ) : sdProductAdsError ? (
                <div className="p-6">
                  <p className="text-sm text-error mb-3">
                    {(sdProductAdsError as Error).message || 'Failed to load SD product ads'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchSdProductAds()}
                    className="text-sm text-brand underline"
                  >
                    Retry
                  </button>
                </div>
              ) : sdProductAdsFiltered.length === 0 ? (
                <div className="p-6 text-sm text-foreground-subtle">
                  {dashboardScheduleFilter
                    ? 'No SD product ads for campaigns overlapping the selected date range.'
                    : 'No SD product ads for current filters.'}
                </div>
              ) : (
                <div className={tc.scrollWrap}>
                  <table className={tc.table}>
                    <thead className={tc.thead}>
                      <tr className="text-left text-foreground-muted">
                        <th className={tc.th}>Ad name</th>
                        <th className={tc.th}>Ad ID</th>
                        <th className={tc.th}>Campaign</th>
                        <th className={tc.th}>Ad group ID</th>
                        <th className={tc.th}>ASIN</th>
                        <th className={tc.th}>State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {renderProductAdRows(sdProductAdsFiltered)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-foreground-subtle">
            SP pagination:{' '}
            {targetsPayload.nextToken ? 'More SP targets available.' : 'No further SP pages.'}
          </p>
          <button
            type="button"
            onClick={() => setTargetNextToken(targetsPayload.nextToken)}
            disabled={!targetsPayload.nextToken || isTargetsLoading}
            className="text-sm px-3 py-1.5 border border-border rounded-lg hover:bg-surface disabled:opacity-50"
          >
            Next page (SP)
          </button>
        </div>
      </div>
    );
  };

  const tabContent = activeTab === 'campaigns' ? (
          campaignsPanel(false)
        ) : activeTab === 'ad-groups' ? (
          adGroupsPanel(false)
        ) : activeTab === 'keywords' ? (
          keywordsPanel(false)
        ) : activeTab === 'targets' ? (
          targetsPanel(false)
        ) : activeTab === 'budget-rules' ? (
          <div className="space-y-5">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div className="space-y-2">
                <p className="text-foreground-muted">
                  Create and manage profile-level {campaignAdProductLabel(budgetRulesProduct)} budget rules.
                </p>
                <div className="flex flex-wrap gap-2">
                  {(['SP', 'SB', 'SD'] as const).map((product) => (
                    <button
                      key={product}
                      type="button"
                      onClick={() => setBudgetRulesProduct(product)}
                      className={`px-3 py-1.5 text-sm rounded-pill border ${
                        budgetRulesProduct === product
                          ? 'bg-brand text-white border-brand'
                          : 'bg-surface text-foreground-muted border-border hover:bg-page'
                      }`}
                    >
                      {campaignAdProductLabel(product)}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => refetchBudgetRules()}
                className="text-sm px-3 py-1.5 border border-border rounded-lg hover:bg-surface"
              >
                Refresh rules
              </button>
            </div>

            <form onSubmit={submitBudgetRule} className="border border-border rounded-xl p-4 bg-surface space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Create budget rule</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Rule type</label>
                  <select
                    value={budgetRuleType}
                    onChange={(e) => setBudgetRuleType(e.target.value as 'EVENT_DAILY' | 'SCHEDULE_WEEKLY' | 'PERFORMANCE_DAILY')}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                    disabled={creatingBudgetRule}
                  >
                    <option value="EVENT_DAILY">Event based daily</option>
                    <option value="SCHEDULE_WEEKLY">Scheduled weekly</option>
                    <option value="PERFORMANCE_DAILY">Performance daily</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Rule name</label>
                  <input
                    value={ruleName}
                    onChange={(e) => setRuleName(e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                    placeholder="Easter 25%"
                    disabled={creatingBudgetRule}
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Increase by %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={increasePct}
                    onChange={(e) => setIncreasePct(e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                    disabled={creatingBudgetRule}
                  />
                </div>
              </div>

              {budgetRuleType === 'EVENT_DAILY' && (
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Event ID</label>
                  <input
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                    placeholder="Amazon event id"
                    disabled={creatingBudgetRule}
                  />
                </div>
              )}

              {budgetRuleType !== 'EVENT_DAILY' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-foreground-muted mb-1">Start date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                      disabled={creatingBudgetRule}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-muted mb-1">End date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                      disabled={creatingBudgetRule}
                    />
                  </div>
                </div>
              )}

              {budgetRuleType === 'SCHEDULE_WEEKLY' && (
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Days of week</label>
                  <div className="flex flex-wrap gap-3">
                    {dayOptions.map((d) => (
                      <label key={d} className="text-sm text-foreground-muted flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={daysOfWeek.includes(d)}
                          onChange={(e) =>
                            setDaysOfWeek((prev) =>
                              e.target.checked ? [...prev, d] : prev.filter((x) => x !== d)
                            )
                          }
                        />
                        {d.slice(0, 3)}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {budgetRuleType === 'PERFORMANCE_DAILY' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-foreground-muted mb-1">ACOS threshold</label>
                    <input
                      type="number"
                      step="0.01"
                      value={threshold}
                      onChange={(e) => setThreshold(e.target.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                      disabled={creatingBudgetRule}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-muted mb-1">Comparison operator</label>
                    <select
                      value={comparisonOperator}
                      onChange={(e) => setComparisonOperator(e.target.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                      disabled={creatingBudgetRule}
                    >
                      <option value="LESS_THAN_OR_EQUAL_TO">Less than or equal to</option>
                      <option value="LESS_THAN">Less than</option>
                      <option value="GREATER_THAN_OR_EQUAL_TO">Greater than or equal to</option>
                      <option value="GREATER_THAN">Greater than</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="border border-chrome-divider rounded-xl p-3 bg-page/50 space-y-2">
                <label className="block text-xs font-medium text-foreground-muted">
                  Link to campaigns (optional)
                </label>
                <input
                  type="search"
                  value={createCampaignSearch}
                  onChange={(e) => setCreateCampaignSearch(e.target.value)}
                  placeholder={CAMPAIGN_SEARCH_PLACEHOLDER}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={creatingBudgetRule}
                />
                {isLoading && activeTab === 'budget-rules' ? (
                  <p className="text-xs text-foreground-subtle">Loading campaigns…</p>
                ) : createFormCampaignOptions.length === 0 ? (
                  <p className="text-xs text-foreground-subtle">
                    No {campaignAdProductLabel(budgetRulesProduct)} campaigns available.
                  </p>
                ) : (
                  <ul className="max-h-40 overflow-y-auto space-y-1 list-none p-0 m-0 border border-border rounded-xl bg-surface divide-y divide-border">
                    {createFormCampaignOptions.map((c) => (
                      <li key={c.campaignId}>
                        <label className="flex items-start gap-2 px-2.5 py-2 text-sm cursor-pointer hover:bg-page">
                          <input
                            type="checkbox"
                            checked={createBudgetRuleCampaignIds.includes(c.campaignId)}
                            onChange={(e) =>
                              toggleCreateBudgetRuleCampaign(c.campaignId, e.target.checked)
                            }
                            disabled={creatingBudgetRule}
                            className="mt-0.5"
                          />
                          <span className="min-w-0">
                            <span className="block font-medium text-foreground truncate">
                              {c.name}
                            </span>
                            <span className="block text-xs text-foreground-subtle font-mono truncate">
                              {c.campaignId}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {createBudgetRuleCampaignIds.length > 0 ? (
                  <p className="text-xs text-foreground-muted">
                    {createBudgetRuleCampaignIds.length} campaign
                    {createBudgetRuleCampaignIds.length !== 1 ? 's' : ''} selected
                  </p>
                ) : null}
              </div>

              {budgetRuleError && <p className="text-sm text-error">{budgetRuleError}</p>}
              <div>
                <button
                  type="submit"
                  disabled={creatingBudgetRule}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white disabled:opacity-50"
                >
                  {creatingBudgetRule ? 'Creating...' : 'Create budget rule'}
                </button>
              </div>
            </form>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Your budget rules</h3>
                {budgetRules.length > 0 && (
                  <span className="text-xs text-foreground-subtle">{budgetRules.length} rule{budgetRules.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              {budgetRulesNextToken && (
                <p className="text-xs text-warning-ink bg-warning-subtle border border-warning/20 rounded-xl px-2 py-1.5">
                  More results may be available (pagination token present). This view shows the first page only.
                </p>
              )}
              <div className="rounded-xl border border-border bg-page/50 p-4">
                {isBudgetRulesLoading ? (
                  <p className="text-sm text-foreground-subtle">Loading budget rules...</p>
                ) : budgetRulesError ? (
                  <p className="text-sm text-error">
                    {(budgetRulesError as Error).message || 'Failed to load budget rules'}
                  </p>
                ) : budgetRules.length === 0 ? (
                  <p className="text-sm text-foreground-subtle">No budget rules found for this profile.</p>
                ) : (
                  <ul className="space-y-3 list-none p-0 m-0">
                    {budgetRules.map((r, idx) => {
                      const id = typeof r.ruleId === 'string' ? r.ruleId : `idx-${idx}`;
                      return (
                        <li key={id}>
                          <BudgetRuleListCard
                            row={r}
                            onEdit={setEditingBudgetRule}
                            onToggleState={toggleBudgetRuleState}
                            togglingState={togglingBudgetRuleId === id}
                            profileId={profileId}
                            product={budgetRulesProduct}
                            profileCampaigns={campaigns}
                            onAssociationsChanged={handleBudgetRuleAssociationsChanged}
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'reports' ? (
          <div className="space-y-5">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Sponsored ads reports</h3>
                <p className="mt-1 text-sm text-foreground-muted">
                  Sync Sponsored Products, Brands, and Display campaign reports. Reuse cached windows where possible.
                  Current month sync runs from the 1st through yesterday.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col justify-end gap-1">
                  <label className="text-xs leading-4 text-foreground-muted">Start date</label>
                  <input
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    className="box-border h-9 border border-border rounded-lg px-3 py-0 text-sm leading-5"
                  />
                </div>
                <div className="flex flex-col justify-end gap-1">
                  <label className="text-xs leading-4 text-foreground-muted">End date</label>
                  <input
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    className="box-border h-9 border border-border rounded-lg px-3 py-0 text-sm leading-5"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => startReportSync('last-two-months')}
                  disabled={reportActionBusy !== null}
                  className="box-border inline-flex h-9 shrink-0 items-center px-4 text-sm font-medium leading-5 rounded-lg bg-brand text-white disabled:opacity-50"
                >
                  {reportActionBusy === 'last-two-months' ? 'Starting...' : 'Sync last 2 months'}
                </button>
                <button
                  type="button"
                  onClick={() => startReportSync('current-month')}
                  disabled={reportActionBusy !== null}
                  className="box-border inline-flex h-9 shrink-0 items-center px-4 text-sm font-medium leading-5 rounded-lg border border-border bg-surface text-foreground disabled:opacity-50"
                >
                  {reportActionBusy === 'current-month' ? 'Starting...' : 'Sync current month to yesterday'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void refetchReportSummary();
                    void refetchReportRows();
                    void refetchKeywordReportRows();
                    void refetchTargetReportRows();
                    if (reportBatchId) void refetchReportBatch();
                  }}
                  disabled={isReportRangeInvalid}
                  className="box-border inline-flex h-9 shrink-0 items-center text-sm px-3 leading-5 border border-border rounded-lg hover:bg-surface"
                >
                  Refresh
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="shrink-0 text-xs font-medium leading-none text-foreground-muted">
                  Ad type
                </span>
                {REPORT_AD_PRODUCT_OPTIONS.map((opt) => {
                  const on = reportAdProductFilters.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggleReportAdProductFilter(opt.id)}
                      className={`inline-flex shrink-0 items-center px-3 py-1.5 rounded-full text-xs font-medium leading-5 border transition-colors ${
                        on
                          ? 'bg-brand text-white border-brand'
                          : 'bg-surface text-foreground-muted border-border hover:bg-page'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {reportActionError && <p className="text-sm text-error">{reportActionError}</p>}
            {isReportRangeInvalid && (
              <p className="text-sm text-error">End date must be on or after start date.</p>
            )}
            {reportBatchError && <p className="text-sm text-error">{(reportBatchError as Error).message}</p>}
            {reportSummaryError && <p className="text-sm text-error">{(reportSummaryError as Error).message}</p>}
            {reportRowsError && <p className="text-sm text-error">{(reportRowsError as Error).message}</p>}
            {keywordReportRowsError && (
              <p className="text-sm text-error">{(keywordReportRowsError as Error).message}</p>
            )}
            {targetReportRowsError && (
              <p className="text-sm text-error">{(targetReportRowsError as Error).message}</p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">Latest sync batch</h4>
                  {isReportBatchLoading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                </div>
                {reportBatch.batch ? (
                  <>
                    <div className="text-sm text-foreground-muted">
                      Status: <span className="font-medium capitalize">{reportBatch.batch.status || 'unknown'}</span>
                      {reportBatch.reusedActiveBatch ? ' (reused active batch)' : ''}
                    </div>
                    <div className="text-xs text-foreground-subtle">
                      Range: {reportBatch.batch.requested_range_start || '-'} to {reportBatch.batch.requested_range_end || '-'}
                    </div>
                    <div className="text-xs text-foreground-subtle">
                      Completed {reportBatch.batch.completed_window_count || 0} | Cached {reportBatch.batch.cached_window_count || 0} | Failed {reportBatch.batch.failed_window_count || 0}
                    </div>
                    <ul className="space-y-2">
                      {reportBatch.requests.map((item, idx) => (
                        <li key={item.id || `${item.window_start}-${item.window_end}-${idx}`} className="rounded-xl border border-border px-3 py-2">
                          <div className="text-sm font-medium text-foreground">
                            {item.window_start || '-'} to {item.window_end || '-'}
                            {item.report_type_id ? (
                              <span className="text-foreground-subtle font-normal"> · {item.report_type_id}</span>
                            ) : null}
                          </div>
                          <div className="text-xs text-foreground-subtle">
                            Status: {item.status || 'unknown'}
                            {item.is_cached ? ' (cached)' : ''}
                            {typeof item.imported_row_count === 'number' ? ` | Rows: ${item.imported_row_count}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-foreground-subtle">Start a sync to track report generation and imports here.</p>
                )}
              </section>

              <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">Selected range summary</h4>
                  {isReportSummaryLoading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                </div>
                <div className="text-xs text-foreground-subtle">
                  Range: {reportSummary.range.startDate || '-'} to {reportSummary.range.endDate || '-'}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">Impressions</div>
                    <div className="font-semibold text-foreground">{formatNumberValue(reportSummary.totals.impressions ?? null, 0)}</div>
                  </div>
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">Clicks</div>
                    <div className="font-semibold text-foreground">{formatNumberValue(reportSummary.totals.clicks ?? null, 0)}</div>
                  </div>
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">Spend</div>
                    <div className="font-semibold text-foreground">{formatCurrencyValue(reportSummary.totals.cost ?? null)}</div>
                  </div>
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">Sales</div>
                    <div className="font-semibold text-foreground">{formatCurrencyValue(reportSummary.totals.sales30d ?? null)}</div>
                  </div>
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">CTR</div>
                    <div className="font-semibold text-foreground">{formatPercentValue(reportSummary.totals.ctr ?? null)}</div>
                  </div>
                  <div className="rounded-xl bg-page px-3 py-2">
                    <div className="text-foreground-subtle">ROAS</div>
                    <div className="font-semibold text-foreground">{formatNumberValue(reportSummary.totals.roas ?? null)}</div>
                  </div>
                </div>
                <div className="text-xs text-foreground-subtle">Imported rows in selected range: {reportSummary.rowCount}</div>
              </section>
            </div>

            <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Campaign report</h4>
                  <p className="text-xs text-foreground-subtle">
                    Range: {reportRows.range.startDate || reportStartDate || '-'} to {reportRows.range.endDate || reportEndDate || '-'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ReportColumnPicker
                    columns={CAMPAIGN_REPORT_COLUMNS}
                    selected={campaignReportColumns}
                    onChange={setCampaignReportColumns}
                    requiredColumnId="campaign"
                    panelTitle="Campaign columns"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      exportCampaignReportCsv(reportRows.rows, {
                        startDate: reportRows.range.startDate || reportStartDate,
                        endDate: reportRows.range.endDate || reportEndDate,
                        selectedColumns: campaignReportColumns,
                        showTypeColumn: reportAdProductFilters.length > 1,
                      })
                    }
                    disabled={isReportRowsLoading || reportRows.rows.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-pill border border-border bg-surface text-foreground hover:bg-page disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export
                  </button>
                  {isReportRowsLoading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                </div>
              </div>
              <div className="text-xs text-foreground-subtle">Campaigns returned: {reportRows.rowCount}</div>
              {reportRows.rows.length === 0 ? (
                <p className="text-sm text-foreground-subtle">No campaign report data found for the selected range.</p>
              ) : (
                <div className="overflow-x-auto max-h-48 overflow-y-auto border border-chrome-divider rounded-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-page text-left text-foreground-muted sticky top-0 z-10">
                      <tr>
                        {visibleCampaignReportTableColumns.map((columnId) => (
                          <th key={columnId} className="px-3 py-2 font-medium">
                            {campaignReportColumnLabel(columnId)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportRows.rows.map((row, index) => (
                        <tr
                          key={`${row.adProduct || 'sp'}-${row.campaignId || 'campaign'}-${index}`}
                          className="border-t border-border"
                        >
                          {visibleCampaignReportTableColumns.map((columnId) => (
                            <td key={columnId} className={reportTableCellClassName(columnId)}>
                              {renderCampaignReportCell(columnId, row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Keyword report</h4>
                  <p className="text-xs text-foreground-subtle">
                    Sponsored Products and Brands keywords. Range:{' '}
                    {keywordReportRows.range.startDate || reportStartDate || '-'} to{' '}
                    {keywordReportRows.range.endDate || reportEndDate || '-'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ReportColumnPicker
                    columns={KEYWORD_REPORT_COLUMNS}
                    selected={keywordReportColumns}
                    onChange={setKeywordReportColumns}
                    requiredColumnId="keyword"
                    panelTitle="Keyword columns"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      exportKeywordReportCsv(keywordReportRows.rows, {
                        startDate: keywordReportRows.range.startDate || reportStartDate,
                        endDate: keywordReportRows.range.endDate || reportEndDate,
                        selectedColumns: keywordReportColumns,
                        showTypeColumn: keywordReportAdProductFiltersActive.length > 1,
                      })
                    }
                    disabled={
                      isKeywordReportRowsLoading ||
                      keywordReportAdProductFiltersActive.length === 0 ||
                      keywordReportRows.rows.length === 0
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-pill border border-border bg-surface text-foreground hover:bg-page disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export
                  </button>
                  {isKeywordReportRowsLoading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                </div>
              </div>
              <div className="text-xs text-foreground-subtle">Keywords returned: {keywordReportRows.rowCount}</div>
              {keywordReportAdProductFiltersActive.length === 0 ? (
                <p className="text-sm text-foreground-subtle">
                  Keyword reports are available for Sponsored Products and Brands. Enable at least one of those ad
                  types above.
                </p>
              ) : keywordReportRows.rows.length === 0 ? (
                <p className="text-sm text-foreground-subtle">No keyword report data found for the selected range.</p>
              ) : (
                <div className="overflow-x-auto max-h-64 overflow-y-auto border border-chrome-divider rounded-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-page text-left text-foreground-muted sticky top-0 z-10">
                      <tr>
                        {visibleKeywordReportTableColumns.map((columnId) => (
                          <th key={columnId} className="px-3 py-2 font-medium">
                            {keywordReportColumnLabel(columnId)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {keywordReportRows.rows.map((row, index) => (
                        <tr
                          key={`${row.adProduct || 'sp'}-${row.keywordId || 'keyword'}-${index}`}
                          className="border-t border-border"
                        >
                          {visibleKeywordReportTableColumns.map((columnId) => (
                            <td key={columnId} className={reportTableCellClassName(columnId)}>
                              {renderKeywordReportCell(columnId, row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Target report</h4>
                  <p className="text-xs text-foreground-subtle">
                    Sponsored Display product and audience targets. Range:{' '}
                    {targetReportRows.range.startDate || reportStartDate || '-'} to{' '}
                    {targetReportRows.range.endDate || reportEndDate || '-'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ReportColumnPicker
                    columns={TARGET_REPORT_COLUMNS}
                    selected={targetReportColumns}
                    onChange={setTargetReportColumns}
                    requiredColumnId="target"
                    panelTitle="Target columns"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      exportTargetReportCsv(targetReportRows.rows, {
                        startDate: targetReportRows.range.startDate || reportStartDate,
                        endDate: targetReportRows.range.endDate || reportEndDate,
                        selectedColumns: targetReportColumns,
                      })
                    }
                    disabled={
                      isTargetReportRowsLoading ||
                      targetReportAdProductFiltersActive.length === 0 ||
                      targetReportRows.rows.length === 0
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-pill border border-border bg-surface text-foreground hover:bg-page disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Export
                  </button>
                  {isTargetReportRowsLoading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                </div>
              </div>
              <div className="text-xs text-foreground-subtle">Targets returned: {targetReportRows.rowCount}</div>
              {targetReportAdProductFiltersActive.length === 0 ? (
                <p className="text-sm text-foreground-subtle">
                  Target reports are available for Sponsored Display. Enable Sponsored Display in the ad type
                  filters above.
                </p>
              ) : targetReportRows.rows.length === 0 ? (
                <p className="text-sm text-foreground-subtle">No target report data found for the selected range.</p>
              ) : (
                <div className="overflow-x-auto max-h-64 overflow-y-auto border border-chrome-divider rounded-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-page text-left text-foreground-muted sticky top-0 z-10">
                      <tr>
                        {visibleTargetReportTableColumns.map((columnId) => (
                          <th key={columnId} className="px-3 py-2 font-medium">
                            {targetReportColumnLabel(columnId)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {targetReportRows.rows.map((row, index) => (
                        <tr
                          key={`${row.adProduct || 'sd'}-${row.targetId || 'target'}-${index}`}
                          className="border-t border-border"
                        >
                          {visibleTargetReportTableColumns.map((columnId) => (
                            <td key={columnId} className={reportTableCellClassName(columnId)}>
                              {renderTargetReportCell(columnId, row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        ) : activeTab === 'compare' ? (
          <SpReportCompareTab profileId={profileId} accountName={accountName} />
        ) : activeTab === 'recommendations' ? (
          <SpRecommendationsTab profileId={profileId} accountName={accountName} />
        ) : (
          <p className="text-sm text-foreground-muted">Unknown section.</p>
        );

  return (
    <>
      {activeTab === 'dashboard' ? (
        <AccountDashboard
          campaignsSection={
            <PageSection
              title="Campaigns"
              actions={
                dashboardReportLoading ? (
                  <span className="inline-flex items-center gap-1.5 text-body-sm text-foreground-muted">
                    <Spinner className="w-3.5 h-3.5" />
                    Loading metrics…
                  </span>
                ) : undefined
              }
            >
              {campaignsPanel(true)}
            </PageSection>
          }
          keywordsSection={
            <LazyMount
              placeholder={
                <PageSection title="Keywords">
                  <p className="text-body-sm text-foreground-muted">Scroll down to load keywords…</p>
                </PageSection>
              }
            >
              <PageSection title="Keywords">{keywordsPanel(true)}</PageSection>
            </LazyMount>
          }
          portfoliosSection={
            <LazyMount
              placeholder={
                <PageSection title="Portfolios">
                  <p className="text-body-sm text-foreground-muted">Scroll down to load portfolios…</p>
                </PageSection>
              }
            >
              <PageSection title="Portfolios">
                <PortfoliosSection profileId={profileId} />
              </PageSection>
            </LazyMount>
          }
          budgetUsageSection={
            <LazyMount
              placeholder={
                <PageSection title="Budget usage alerts (Marketing Stream)">
                  <p className="text-body-sm text-foreground-muted">Scroll down to load budget usage…</p>
                </PageSection>
              }
            >
              <PageSection title="Budget usage alerts (Marketing Stream)">
                <BudgetUsageSection
                  profileId={profileId}
                  startDate={dashboardStartDate}
                  endDate={dashboardEndDate}
                  campaigns={campaigns}
                  invalidRange={isDashboardRangeInvalid}
                />
              </PageSection>
            </LazyMount>
          }
        />
      ) : activeTab === 'compare' || activeTab === 'recommendations' ? (
        tabContent
      ) : activeTab === 'reports' ? (
        <div className="mx-auto w-full max-w-[1380px] px-6 pb-8 pt-1">
          <header className="mb-4">
            <h1 className="text-2xl font-semibold text-foreground">Reports</h1>
          </header>
          {tabContent}
        </div>
      ) : activeTab === 'budget-rules' ? (
        <div className="mx-auto max-w-6xl px-6 pb-6 pt-1">
          <header className="mb-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-semibold text-foreground">Budget rules</h1>
              <Badge variant="neutral" className="normal-case tracking-normal font-medium">
                Amazon
              </Badge>
              <span className="text-sm text-foreground-subtle">|</span>
              <span className="font-mono text-sm text-foreground-muted">{profileId}</span>
            </div>
          </header>
          {tabContent}
        </div>
      ) : (
        <div className="mx-auto max-w-6xl space-y-8 p-6">
          <AccountPageHeader
            title={accountTabTitle(activeTab)}
            accountName={accountName}
            profileId={profileId}
          />
          {tabContent}
        </div>
      )}

      {bulkBudgetPreviewRows ? (
        <BulkCampaignBudgetConfirmModal
          rows={bulkBudgetPreviewRows}
          pending={bulkBudgetApplying}
          formatCurrency={formatCurrency}
          onConfirm={() => void handleConfirmBulkBudget()}
          onCancel={() => {
            if (!bulkBudgetApplying) setBulkBudgetPreviewRows(null);
          }}
        />
      ) : null}
      {hourlyCampaign && (
        <HourlyModal
          key={`${hourlyCampaign.adProduct}-${hourlyCampaign.campaignId}`}
          campaign={hourlyCampaign}
          profileId={profileId}
          isOpen
          onClose={() => setHourlyCampaign(null)}
        />
      )}
      <CampaignTypePickerModal
        profileId={profileId}
        isOpen={createCampaignPickerOpen}
        onClose={() => setCreateCampaignPickerOpen(false)}
      />
      <BudgetRuleEditModal
        profileId={profileId}
        product={budgetRulesProduct}
        row={editingBudgetRule}
        isOpen={!!editingBudgetRule}
        onClose={() => setEditingBudgetRule(null)}
        profileCampaigns={campaigns}
        onAssociationsChanged={handleBudgetRuleAssociationsChanged}
      />
    </>
  );
}

